import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import styled, { keyframes, css } from "styled-components";
import mammoth from "mammoth";
import theme from "../../../../assets/theme";
import CaseService from "../../../../services/CaseService";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import BundleService from "../../../../services/BundleService";
import {
  ModalBackdrop,
  ModalCard,
  ModalAccent,
  ModalBody,
  ModalHeader,
  ModalHeaderContent,
  ModalCloseBtn,
  ModalTitle,
  ModalSubtitle,
  ModalFooter,
  ModalCancelBtn,
  FieldGroup,
  FieldInput,
  IcoX,
  G,
} from "./styles";
import { Page as PdfPage } from "react-pdf";
import { acquirePdf, getCachedPdf } from "./pdfDocumentCache";
import {
  drawPage,
  getCachedPage,
  isCancelled,
  releaseSource,
  renderPage,
} from "./pdfPageRenderer";
import { getPdfPageCount } from "../../../../helpers/pdfPageCountCache";
import {
  RedactionLayer,
  buildRedactedPdf,
  copyProtectPdfBytes,
  downloadBlob,
  makeRedactedTextRenderer,
  rectsToRedactions,
  redactedFileName,
  useRedactions,
} from "./redaction";
import * as PDFJS from "pdfjs-dist/legacy/build/pdf";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";

PDFJS.GlobalWorkerOptions.isEvalSupported = false;
PDFJS.GlobalWorkerOptions.workerSrc = workerSrc;

const HIGHLIGHT_COLORS = [
  {
    key: "yellow",
    label: "Yellow",
    bg: "#F59E0B",
    alpha: "rgba(245,158,11,0.28)",
  },
  {
    key: "green",
    label: "Green",
    bg: "#10B981",
    alpha: "rgba(16,185,129,0.28)",
  },
  { key: "blue", label: "Blue", bg: "#3B82F6", alpha: "rgba(59,130,246,0.25)" },
  { key: "pink", label: "Pink", bg: "#EC4899", alpha: "rgba(236,72,153,0.25)" },
  {
    key: "orange",
    label: "Orange",
    bg: "#F97316",
    alpha: "rgba(249,115,22,0.28)",
  },
];

const PAGE_GAP = 22;
const RENDER_BUFFER = 2;
// Keeps the reading position while users move between already-open document tabs.
// This intentionally lives outside the component so changing the active tab does
// not discard the previous document's position.
const documentPagePositions = new Map();

const getDocumentPagePositionKey = (doc = {}) =>
  String(doc.id || doc.sourceDocId || doc.s3url || doc.name || "");

// ── URL page param helpers (used for "open in new tab") ──────────────────
const getUrlPageParam = () => {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("page");
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const clearUrlPageParam = () => {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("page")) return;
    url.searchParams.delete("page");
    const next = url.pathname + (url.search || "") + url.hash;
    window.history.replaceState({ path: next }, "", next);
  } catch {
    // ignore
  }
};

const buildShareableDocUrl = (page) => {
  if (typeof window === "undefined") return "";
  try {
    const url = new URL(window.location.href);
    if (page) {
      url.searchParams.set("page", String(page));
    } else {
      url.searchParams.delete("page");
    }
    return url.toString();
  } catch {
    return window.location.href;
  }
};

const VIS_LEVELS = [
  {
    key: "all",
    name: "Shared with All parties",
    desc: "All parties can view",
    icon: "🌐",
    bg: "#D1FAE5",
    color: "#065F46",
  },
  {
    key: "others",
    name: "Shared with Case managers & Neutral",
    desc: "Case managers & Neutral can view",
    icon: "👥",
    bg: "#DBEAFE",
    color: "#1E40AF",
  },
  {
    key: "me",
    name: "Shared with My Team",
    desc: "Case managers & Co-case managers can view",
    icon: "👤",
    bg: "#DBEAFE",
    color: "#1E40AF",
  },
  {
    key: "strictMode",
    name: "Private",
    desc: "Only you can see this document",
    icon: "🔒",
    bg: "#FEE2E2",
    color: "#991B1B",
  },
];

const getExhibitId = (doc) => {
  if (!doc) return null;
  return (
    doc.exhibitId ||
    doc.exhibit_id ||
    doc.exhibitCode ||
    doc.exhibit_code ||
    doc.properties?.exhibitId ||
    doc.properties?.exhibit_id ||
    doc.properties?.exhibitNo ||
    doc.properties?.exhibit_no ||
    null
  );
};

const ZOOM_PRESETS = [30, 40, 50, 75, 100, 125, 150, 175, 200];

const VIEWER_SHORTCUTS = [
  [["←", "→"], "Previous / next page"],
  [["+", "−"], "Zoom in / out"],
  [["Ctrl", "Scroll"], "Zoom with mouse or trackpad"],
  [["0"], "Fit page"],
  [["W"], "Fit width"],
  [["V"], "Toggle page / scroll view"],
  [["N"], "Add note mode"],
  [["P"], "Toggle document panel"],
  [["?"], "Show this help"],
  [["Esc"], "Exit mode / close viewer"],
];

let _nextId = 1;
const uid = () => `h${_nextId++}`;

function detectFileType(url = "") {
  const c = url.split("?")[0].toLowerCase();
  if (c.endsWith(".pdf")) return "pdf";
  if (c.endsWith(".docx")) return "docx";
  if (c.endsWith(".doc")) return "doc";
  if (/\.(jpe?g|png|gif|webp|svg|bmp)$/.test(c)) return "image";
  if (c.endsWith(".txt")) return "txt";
  if (/\.(xlsx|xls|pptx|ppt)$/.test(c)) return "office";
  if (/\.(mp4|mkv|mov|webm)$/.test(c)) return "video";
  if (/\.(mp3|wav|ogg|m4a)$/.test(c)) return "audio";
  return "unknown";
}

async function fetchDocxHtml(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer: buf });
  return result.value;
}

const storageKey = (id) => `doc_annotations_${id}`;
const notesKey = (id) => `doc_notes_${id}`;
const hyperlinksKey = (id) => `doc_hyperlinks_${id}`;

function loadAnnotations(docId) {
  try {
    return JSON.parse(localStorage.getItem(storageKey(docId)) || "[]");
  } catch {
    return [];
  }
}
function saveAnnotations(docId, v) {
  try {
    localStorage.setItem(storageKey(docId), JSON.stringify(v));
  } catch {}
}
function loadNotes(docId) {
  try {
    return JSON.parse(localStorage.getItem(notesKey(docId)) || "[]");
  } catch {
    return [];
  }
}
function saveNotes(docId, v) {
  try {
    localStorage.setItem(notesKey(docId), JSON.stringify(v));
  } catch {}
}
function loadHyperlinks(docId) {
  try {
    return JSON.parse(localStorage.getItem(hyperlinksKey(docId)) || "[]");
  } catch {
    return [];
  }
}
function saveHyperlinks(docId, v) {
  try {
    localStorage.setItem(hyperlinksKey(docId), JSON.stringify(v));
  } catch {}
}

function validatePageNumber(pageStr, maxPages) {
  const trimmed = String(pageStr ?? "").trim();
  if (!trimmed) {
    return "Page number is required";
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Please enter a valid whole number (positive integers only)";
  }
  const pageNum = Number(trimmed);
  if (pageNum <= 0) {
    return "Page number must be 1 or greater";
  }
  if (
    maxPages &&
    Number.isFinite(maxPages) &&
    maxPages > 0 &&
    pageNum > maxPages
  ) {
    return `Page number cannot exceed total pages (${maxPages})`;
  }
  return null;
}

function getFormattedHyperlinkTarget(targetDoc, pageNumber) {
  if (!targetDoc) return String(pageNumber || "");
  const isTrial = Boolean(
    targetDoc.bundleName ||
    targetDoc.folderName ||
    targetDoc.bundleCode ||
    (targetDoc.id && String(targetDoc.id).startsWith("trial-")),
  );

  if (isTrial) {
    const tabRef =
      targetDoc.tabRef ||
      (targetDoc.bundleCode &&
      targetDoc.tabNum != null &&
      targetDoc.tabNum !== ""
        ? `${targetDoc.bundleCode}/${targetDoc.tabNum}`
        : targetDoc.tabNum != null && targetDoc.tabNum !== ""
          ? String(targetDoc.tabNum)
          : "");
    return tabRef
      ? `${tabRef}/${pageNumber}`
      : `${targetDoc.name}/${pageNumber}`;
  } else {
    const ref = targetDoc.documentNo || targetDoc.ref || targetDoc.tabRef || "";
    return ref ? `${ref}/${pageNumber}` : `${targetDoc.name}/${pageNumber}`;
  }
}

// Rects are stored relative to the PAGE CANVAS so overlays rendered
// inside each page's local coordinate system line up correctly.
function getRectsRelativeToWrap(range, _pdfWrapEl, canvasEl) {
  // Use the canvas element of the specific page as the coordinate origin.
  // If we can't find it, fall back to the selection's bounding rect origin.
  const originEl = canvasEl || _pdfWrapEl?.querySelector("canvas");
  const originRect = originEl
    ? originEl.getBoundingClientRect()
    : range.getBoundingClientRect();

  const rects = Array.from(range.getClientRects())
    .filter((r) => r.width > 0 && r.height > 0)
    .map((r) => ({
      left: r.left - originRect.left,
      top: r.top - originRect.top,
      width: r.width,
      height: r.height,
    }));

  // canvasOffset is now always {0,0} because rects are already canvas-relative
  return { rects, canvasOffset: { left: 0, top: 0 } };
}

function getViewportAnchorFromRange(range) {
  const rects = Array.from(range.getClientRects()).filter(
    (r) => r.width > 0 && r.height > 0,
  );
  if (!rects.length) {
    const br = range.getBoundingClientRect();
    return {
      x: br.left + br.width / 2,
      y: br.bottom,
      width: br.width,
      height: br.height,
    };
  }
  const last = rects[rects.length - 1];
  return {
    x: last.left + last.width / 2,
    y: last.bottom,
    width: last.width,
    height: last.height,
  };
}

async function downloadPdfWithAnnotations(
  url,
  annotations,
  notes,
  docName,
  redactions = [],
) {
  let jsPDF = window.jspdf?.jsPDF;
  if (!jsPDF) {
    await new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = res;
      s.onerror = rej;
      document.head.appendChild(s);
    });
    jsPDF = window.jspdf.jsPDF;
  }

  const RENDER_SCALE = 2.5;
  const { promise: pdfPromise, release: releasePdf } = acquirePdf(url);
  try {
    const pdfDoc = await pdfPromise;
    const numPages = pdfDoc.numPages;

    const firstPage = await pdfDoc.getPage(1);
    const vp0 = firstPage.getViewport({ scale: RENDER_SCALE });

    const pdf = new jsPDF({
      orientation: vp0.width > vp0.height ? "l" : "p",
      unit: "px",
      format: [vp0.width, vp0.height],
      compress: true,
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: RENDER_SCALE });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: ctx, viewport }).promise;

      ctx.fillStyle = "#000000";
      redactions
        .filter((r) => r.page === pageNum)
        .forEach((r) =>
          ctx.fillRect(
            r.x * canvas.width,
            r.y * canvas.height,
            r.w * canvas.width + 1,
            r.h * canvas.height + 1,
          ),
        );

      const pageAnns = annotations.filter((a) => a.page === pageNum);
      for (const ann of pageAnns) {
        if (!ann.rects?.length) continue;
        const color =
          HIGHLIGHT_COLORS.find((c) => c.key === ann.color) ||
          HIGHLIGHT_COLORS[0];
        const factor = RENDER_SCALE / (ann.cssScale || 1);
        const co = ann.canvasOffset || { left: 0, top: 0 };
        ctx.save();
        ctx.globalAlpha = 0.35;
        ctx.fillStyle = color.bg;
        for (const r of ann.rects) {
          ctx.fillRect(
            (r.left - co.left) * factor,
            (r.top - co.top) * factor,
            r.width * factor,
            r.height * factor,
          );
        }
        ctx.restore();
      }

      const pageNotes = notes.filter((n) => n.page === pageNum && n.x != null);
      for (const n of pageNotes) {
        const factor = RENDER_SCALE / (n.cssScale || 1);
        const nx = n.x * factor,
          ny = n.y * factor;
        const W = 180 * RENDER_SCALE,
          H = 90 * RENDER_SCALE;
        ctx.save();
        ctx.globalAlpha = 0.95;
        ctx.fillStyle = "#FEF9C3";
        ctx.strokeStyle = "#F59E0B";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(nx, ny, W, H, 8);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = "#444";
        ctx.font = `bold ${11 * RENDER_SCALE}px sans-serif`;
        ctx.fillText("NOTE", nx + 12 * RENDER_SCALE, ny + 22 * RENDER_SCALE);
        ctx.font = `${10 * RENDER_SCALE}px sans-serif`;
        const words = n.text.split(" ");
        let line = "",
          lineY = ny + 42 * RENDER_SCALE;
        for (const word of words) {
          const test = line + word + " ";
          if (ctx.measureText(test).width > W - 24 * RENDER_SCALE && line) {
            ctx.fillText(line.trim(), nx + 12 * RENDER_SCALE, lineY);
            line = word + " ";
            lineY += 13 * RENDER_SCALE;
            if (lineY > ny + H - 15 * RENDER_SCALE) break;
          } else {
            line = test;
          }
        }
        if (line) ctx.fillText(line.trim(), nx + 12 * RENDER_SCALE, lineY);
        ctx.restore();
      }

      const pageAnnsWithComment = pageAnns.filter((a) => a.comment?.trim());
      for (const ann of pageAnnsWithComment) {
        if (!ann.rects?.length) continue;
        const factor = RENDER_SCALE / (ann.cssScale || 1);
        const co = ann.canvasOffset || { left: 0, top: 0 };
        const firstRect = ann.rects[0];
        const commentX = (firstRect.left - co.left) * factor + 6;
        const commentY = (firstRect.top - co.top) * factor - 18;
        ctx.save();
        ctx.fillStyle = "#1e293b";
        ctx.font = `bold ${10 * RENDER_SCALE}px sans-serif`;
        ctx.fillText(
          "💬 " + ann.comment,
          commentX,
          Math.max(25 * RENDER_SCALE, commentY),
        );
        ctx.restore();
      }

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      if (pageNum === 1) {
        pdf.addImage(imgData, "JPEG", 0, 0, vp0.width, vp0.height);
      } else {
        pdf.addPage(
          [viewport.width, viewport.height],
          viewport.width > viewport.height ? "l" : "p",
        );
        pdf.addImage(imgData, "JPEG", 0, 0, viewport.width, viewport.height);
      }
    }

    const fileName =
      (docName || "document").replace(/\.[^.]+$/, "") + "-annotated.pdf";
    if (redactions.length) {
      // Lock copying so the viewer's OCR can't lift text off a redacted copy.
      const bytes = await copyProtectPdfBytes(pdf.output("arraybuffer"));
      downloadBlob(new Blob([bytes], { type: "application/pdf" }), fileName);
    } else {
      pdf.save(fileName);
    }
  } finally {
    releasePdf();
  }
}

// ─── SelectionTip ─────────────────────────────────────────────────────────────

function SelectionTip({ anchorPoint, text, onSave, onCancel, onRedact }) {
  const [comment, setComment] = useState("");
  const [color, setColor] = useState("yellow");
  const tipRef = useRef(null);

  if (!anchorPoint) return null;

  const left = Math.max(
    12,
    Math.min(anchorPoint.left - 140, window.innerWidth - 290),
  );
  let top = anchorPoint.bottom + 12;
  if (top > window.innerHeight - 260) top = anchorPoint.top - 240;
  top = Math.max(12, top);

  return (
    <TipBox ref={tipRef} style={{ position: "fixed", top, left, zIndex: 9999 }}>
      <TipPreview>
        "{text.length > 68 ? text.slice(0, 68) + "…" : text}"
      </TipPreview>
      <TipLabel>Annotation Colour</TipLabel>
      <TipColorRow>
        {HIGHLIGHT_COLORS.map((c) => (
          <TipColorDot
            key={c.key}
            $bg={c.bg}
            $selected={color === c.key}
            onClick={() => setColor(c.key)}
            title={c.label}
          />
        ))}
      </TipColorRow>
      <TipInput
        placeholder="Add comment (optional)…"
        value={comment}
        autoFocus
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSave({ comment, color });
          }
        }}
      />
      <TipActions>
        <TipCancel onClick={onCancel}>Cancel</TipCancel>
        {onRedact && (
          <TipRedact onClick={onRedact} title="Black out the selected text">
            Redact
          </TipRedact>
        )}
        <TipSave onClick={() => onSave({ comment, color })}>
          Save Annotate
        </TipSave>
      </TipActions>
    </TipBox>
  );
}

// ─── NoteInputTip ─────────────────────────────────────────────────────────────

function NoteInputTip({ anchorPoint, page, onSave, onCancel }) {
  const [text, setText] = useState("");
  if (!anchorPoint) return null;

  return (
    <TipBox
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
        minWidth: 280,
      }}
    >
      <TipLabel>Note — Page {page ?? "Document"}</TipLabel>
      <NoteInputTA
        placeholder="Type your note here..."
        value={text}
        autoFocus
        rows={4}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.ctrlKey && e.key === "Enter" && text.trim()) onSave(text);
        }}
      />
      <div style={{ fontSize: 10, color: "#94A3B8", marginBottom: 10 }}>
        Ctrl + Enter to save
      </div>
      <TipActions>
        <TipCancel onClick={onCancel}>Cancel</TipCancel>
        <TipSave onClick={() => text.trim() && onSave(text)}>Add Note</TipSave>
      </TipActions>
    </TipBox>
  );
}

// ─── StickyNotePin ────────────────────────────────────────────────────────────

const StickyNotePin = React.memo(function StickyNotePin({
  note,
  zoom,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const scale = (zoom || 100) / 100;
  const factor = scale / (note.cssScale || 1);
  return (
    <div
      style={{
        position: "absolute",
        left: note.x * factor,
        top: note.y * factor,
        zIndex: 50,
        pointerEvents: "all",
      }}
    >
      <NotePin onClick={() => setOpen((o) => !o)} title="Click to view note" />
      {open && (
        <StickyCard>
          <StickyText>{note.text}</StickyText>
          <StickyMeta>
            Page {note.page ?? "DOCX"} {note.time && `· ${note.time}`}
          </StickyMeta>
          <StickyDelete onClick={() => onDelete(note.id)}>
            Delete note
          </StickyDelete>
        </StickyCard>
      )}
    </div>
  );
});

// ─── HyperlinkPin ─────────────────────────────────────────────────────────────

const HyperlinkPin = React.memo(function HyperlinkPin({
  hyperlink,
  zoom,
  onOpen,
  onDelete,
}) {
  const pinRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({ x: "right", y: "top" });
  const scale = (zoom || 100) / 100;
  const factor = scale / (hyperlink.cssScale || 1);

  const calculatePlacement = useCallback(() => {
    if (!pinRef.current) return;
    const pinRect = pinRef.current.getBoundingClientRect();

    // 1. Find the page boundary ([data-pdf-page] or immediate page canvas container)
    const pageEl =
      pinRef.current.closest("[data-pdf-page]") ||
      pinRef.current.closest("[data-docx-page]") ||
      pinRef.current.parentElement;
    const pageRect = pageEl ? pageEl.getBoundingClientRect() : null;

    // 2. Find the scrollable viewer viewport (CanvasArea / scroll wrapper)
    let scrollEl = pinRef.current.parentElement;
    while (scrollEl && scrollEl !== document.body) {
      const cs = window.getComputedStyle(scrollEl);
      if (/(auto|scroll)/.test(cs.overflow + cs.overflowX + cs.overflowY)) {
        break;
      }
      scrollEl = scrollEl.parentElement;
    }
    const scrollRect = scrollEl ? scrollEl.getBoundingClientRect() : null;

    // Available space to the right & left
    // We constrain against the page right edge AND viewer viewport right edge
    const rightLimit = Math.min(
      window.innerWidth || 1920,
      scrollRect ? scrollRect.right - 10 : Infinity,
      pageRect ? pageRect.right - 10 : Infinity,
    );
    const leftLimit = Math.max(
      0,
      scrollRect ? scrollRect.left + 10 : 0,
      pageRect ? pageRect.left + 10 : 0,
    );

    const spaceRight = rightLimit - pinRect.right;
    const spaceLeft = pinRect.left - leftLimit;

    // Available space to the bottom & top
    const bottomLimit = Math.min(
      window.innerHeight || 1080,
      scrollRect ? scrollRect.bottom - 12 : Infinity,
      pageRect ? pageRect.bottom - 12 : Infinity,
    );
    const topLimit = Math.max(
      0,
      scrollRect ? scrollRect.top + 12 : 0,
      pageRect ? pageRect.top + 12 : 0,
    );
    const spaceBottom = bottomLimit - pinRect.bottom;
    const spaceTop = pinRect.top - topLimit;

    const CARD_WIDTH = 260;
    const CARD_HEIGHT = 130;

    let posX = "right";
    if (spaceRight < CARD_WIDTH && spaceLeft >= CARD_WIDTH) {
      posX = "left";
    } else if (spaceRight < CARD_WIDTH && spaceLeft < CARD_WIDTH) {
      // Pick whichever side has more room
      posX = spaceLeft > spaceRight ? "left" : "right";
    }

    let posY = "center";
    if (spaceBottom < CARD_HEIGHT && spaceTop >= CARD_HEIGHT) {
      posY = "bottom";
    } else if (spaceTop < CARD_HEIGHT / 2 && spaceBottom >= CARD_HEIGHT) {
      posY = "top";
    } else if (spaceBottom < CARD_HEIGHT / 2 && spaceTop < CARD_HEIGHT / 2) {
      posY = spaceBottom > spaceTop ? "top" : "bottom";
    }

    setPlacement({ x: posX, y: posY });
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open) {
      calculatePlacement();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useLayoutEffect(() => {
    if (open) {
      calculatePlacement();
    }
  }, [open, calculatePlacement]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (pinRef.current && !pinRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [open]);

  return (
    <div
      ref={pinRef}
      style={{
        position: "absolute",
        left: hyperlink.x * factor,
        top: hyperlink.y * factor,
        zIndex: open ? 250 : 55,
        pointerEvents: "all",
      }}
      data-hyperlinkpin="1"
    >
      <HyperlinkPinBtn onClick={handleToggle} title="Click to view hyperlink">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ffffff"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      </HyperlinkPinBtn>
      {open && (
        <HyperlinkPinCard $placementX={placement.x} $placementY={placement.y}>
          <HyperlinkPinTitle title={hyperlink.targetDoc?.name}>
            {hyperlink.targetDoc?.name || "Unknown Document"}
          </HyperlinkPinTitle>
          {hyperlink.time && (
            <HyperlinkPinMeta>{hyperlink.time}</HyperlinkPinMeta>
          )}
          <HyperlinkPinActions>
            <HyperlinkPinOpenBtn
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                if (hyperlink.targetDoc)
                  onOpen(
                    hyperlink.targetDoc,
                    hyperlink.targetPage || hyperlink.targetDoc.targetPage,
                  );
              }}
            >
              Open document
            </HyperlinkPinOpenBtn>
            <HyperlinkPinDeleteBtn
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onDelete(hyperlink.id);
              }}
            >
              Delete
            </HyperlinkPinDeleteBtn>
          </HyperlinkPinActions>
        </HyperlinkPinCard>
      )}
    </div>
  );
});

// ─── PdfSkeleton (placeholder for unrendered pages) ───────────────────────────

function PdfPagePlaceholder({ width, height }) {
  return (
    <PlaceholderWrap style={{ width, height }}>
      <SkeletonShimmer />
      <PlaceholderInner>
        <SkeletonBar style={{ width: "55%", height: 14, marginBottom: 20 }} />
        <SkeletonBar style={{ width: "100%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "92%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "85%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "78%", height: 9, marginBottom: 24 }} />
        <SkeletonBar style={{ width: "100%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "95%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "60%", height: 9, marginBottom: 24 }} />
        <SkeletonBar style={{ width: "100%", height: 90, marginBottom: 20 }} />
        <SkeletonBar style={{ width: "90%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "70%", height: 9 }} />
      </PlaceholderInner>
    </PlaceholderWrap>
  );
}

function buildPageLayout(pageSizes, zoom) {
  const scale = zoom / 100;
  let top = 0;
  let maxWidth = 0;
  const pages = pageSizes.map((size, index) => {
    const width = Math.max(1, Math.round(size.width * scale));
    const height = Math.max(1, Math.round(size.height * scale));
    const page = { pageNumber: index + 1, top, width, height };
    top += height + PAGE_GAP;
    maxWidth = Math.max(maxWidth, width);
    return page;
  });
  return {
    pages,
    width: maxWidth,
    height: Math.max(0, top - PAGE_GAP),
  };
}

function findPageIndexAtOffset(pages, offset) {
  if (!pages.length) return 0;
  let low = 0;
  let high = pages.length - 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const page = pages[mid];
    if (offset < page.top) high = mid - 1;
    else if (offset >= page.top + page.height + PAGE_GAP) low = mid + 1;
    else return mid;
  }
  return Math.max(0, Math.min(pages.length - 1, low));
}

// ─── VirtualPdfPage ─────────────────────────────────────────────────────────
// The page canvas comes from pdfPageRenderer (priority queue + bitmap cache,
// as in JustCue): a cached page draws in the same frame, and on zoom the old
// pixels stay up until the sharper render lands. react-pdf's <Page> renders
// only the text layer on top, so text selection and highlights work as before.
const VirtualPdfPage = React.memo(function VirtualPdfPage({
  pdf,
  docKey,
  deferred = false,
  pageNumber,
  zoom,
  width,
  height,
  annotations,
  notes,
  onDeleteAnnot,
  onDeleteNote,
  hyperlinks,
  onOpenHyperlink,
  onDeleteHyperlink,
  redactions,
  redactMode = false,
  onAddRedactions,
  onDeleteRedaction,
}) {
  const [rendered, setRendered] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const taskRef = useRef(null);
  // Off-screen buffer pages render only when no visible page is waiting.
  const priority = deferred ? "idle" : "visible";
  const priorityRef = useRef(priority);
  priorityRef.current = priority;

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return undefined;
    setRenderError(null);
    const draw = (result) => {
      try {
        drawPage(canvas, result);
        return true;
      } catch (err) {
        return false;
      }
    };

    const cached = getCachedPage(docKey, pageNumber, width);
    if (cached && draw(cached)) {
      setRendered(true);
      return undefined;
    }

    let active = true;
    const task = renderPage({
      pdf,
      docKey,
      pageNumber,
      cssWidth: width,
      priority: priorityRef.current,
    });
    taskRef.current = task;
    task.promise.then(
      (result) => {
        if (active) {
          if (draw(result)) setRendered(true);
          else setRenderError("This page could not be drawn.");
        }
        releaseSource(result.source);
      },
      (err) => {
        if (!active || isCancelled(err)) return;
        setRenderError(err?.message || "This page could not be rendered.");
      },
    );
    return () => {
      active = false;
      taskRef.current = null;
      task.cancel();
    };
  }, [pdf, docKey, pageNumber, width, renderAttempt]);

  // A buffer page scrolled into view jumps ahead of the remaining idle work.
  useEffect(() => {
    if (priority === "visible" && taskRef.current) taskRef.current.promote();
  }, [priority]);

  const pageAnnotations = annotations.filter(
    (a) => a.page === pageNumber && a.rects?.length > 0,
  );
  const pageNotes = notes.filter((n) => n.x != null && n.page === pageNumber);
  const pageHyperlinks = (hyperlinks || []).filter(
    (h) => h.x != null && h.page === pageNumber,
  );
  const pageRedactions = useMemo(
    () => (redactions || []).filter((r) => r.page === pageNumber),
    [redactions, pageNumber],
  );
  // Strip redacted characters out of the selectable text layer.
  const redactedTextRenderer = useMemo(
    () =>
      pageRedactions.length
        ? makeRedactedTextRenderer(pageRedactions)
        : undefined,
    [pageRedactions],
  );

  return (
    <PdfPageContainer
      data-pdf-page={pageNumber}
      ref={wrapperRef}
      style={{ margin: 0 }}
    >
      <div
        style={{
          position: "relative",
          width,
          height,
          minWidth: width,
          minHeight: height,
        }}
      >
        {!rendered && <PdfPagePlaceholder width={width} height={height} />}
        {renderError && (
          <div
            role="alert"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              zIndex: 10,
            }}
          >
            <strong
              style={{ display: "block", fontSize: 13, color: "#dc2626" }}
            >
              Page {pageNumber} did not load
            </strong>
            <span style={{ fontSize: 11, color: "#64748b" }}>
              {renderError}
            </span>
            <button
              onClick={() => setRenderAttempt((value) => value + 1)}
              style={{
                display: "block",
                margin: "10px auto 0",
                padding: "4px 10px",
                fontSize: 11,
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        )}

        <canvas
          ref={canvasRef}
          dir="ltr"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width,
            height,
            display: rendered ? "block" : "none",
            userSelect: "none",
          }}
        />
        {rendered && (
          <div style={{ position: "absolute", top: 0, left: 0, width, height }}>
            <PdfPage
              key={renderAttempt}
              className="dv-pdf-text-page"
              pdf={pdf}
              pageNumber={pageNumber}
              width={width}
              renderMode="none"
              renderAnnotationLayer={false}
              renderTextLayer={true}
              customTextRenderer={redactedTextRenderer}
              loading={null}
              error={null}
            />
          </div>
        )}

        {/* Annotations overlay */}
        {rendered &&
          pageAnnotations.map((ann) => (
            <PdfAnnotOverlay
              key={ann.id}
              ann={ann}
              zoom={zoom}
              color={
                HIGHLIGHT_COLORS.find((c) => c.key === ann.color) ||
                HIGHLIGHT_COLORS[0]
              }
              onDelete={onDeleteAnnot}
            />
          ))}

        {/* Redactions */}
        {rendered && (
          <RedactionLayer
            page={pageNumber}
            boxes={pageRedactions}
            editable={redactMode}
            onAdd={onAddRedactions}
            onDelete={onDeleteRedaction}
          />
        )}

        {/* Sticky notes */}
        {rendered &&
          pageNotes.map((n) => (
            <div key={n.id} data-notepin="1">
              <StickyNotePin note={n} zoom={zoom} onDelete={onDeleteNote} />
            </div>
          ))}

        {/* Hyperlink pins */}
        {rendered &&
          pageHyperlinks.map((h) => (
            <HyperlinkPin
              key={h.id}
              hyperlink={h}
              zoom={zoom}
              onOpen={onOpenHyperlink}
              onDelete={onDeleteHyperlink}
            />
          ))}
      </div>
    </PdfPageContainer>
  );
});

function PdfViewer({
  url,
  currentPage,
  setCurrentPage,
  setNumPages,
  numPages,
  zoom,
  annotations,
  notes,
  onAddAnnot,
  onDeleteAnnot,
  onAddNote,
  onDeleteNote,
  noteMode,
  scrollContainerRef,
  scrollTargetPage,
  onScrollTargetConsumed,
  pageSize,
  setPageSize,
  isProgrammaticScroll,
  singlePageView = false,
  hyperlinkMode,
  hyperlinks,
  onAddHyperlink,
  onOpenHyperlink,
  onDeleteHyperlink,
  redactMode = false,
}) {
  const [pending, setPending] = useState(null);
  const [redactions, setRedactions] = useRedactions(url);
  const addRedactions = useCallback(
    (list) => setRedactions((prev) => [...prev, ...list]),
    [setRedactions],
  );
  const deleteRedaction = useCallback(
    (id) => setRedactions((prev) => prev.filter((r) => r.id !== id)),
    [setRedactions],
  );
  const [notePending, setNotePending] = useState(null);
  // PDFDocumentProxy from the shared cache; reopened documents start loaded.
  const [pdfDoc, setPdfDoc] = useState(() => getCachedPdf(url));
  const [loadError, setLoadError] = useState(null);
  const [pageSizes, setPageSizes] = useState([]);
  const [renderRange, setRenderRange] = useState({
    start: 1,
    end: 3,
    visibleStart: 1,
    visibleEnd: 1,
  });
  const pdfWrapRef = useRef(null);
  const offsetTopRef = useRef(0);
  const rafRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  // A document with any redaction can't be copied from at all. Text can
  // still be selected (for highlights), but copy, cut and drag are blocked.
  const isRedacted = redactions.length > 0;
  useEffect(() => {
    if (!isRedacted) return undefined;
    const touchesPdf = () => {
      const wrap = pdfWrapRef.current;
      const sel = window.getSelection();
      if (!wrap || !sel || sel.isCollapsed) return false;
      for (let i = 0; i < sel.rangeCount; i++) {
        if (sel.getRangeAt(i).intersectsNode(wrap)) return true;
      }
      return false;
    };
    const block = (e) => {
      if (e.type !== "dragstart" && !touchesPdf()) return;
      if (e.type === "dragstart" && !pdfWrapRef.current?.contains(e.target)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (e.clipboardData) e.clipboardData.setData("text/plain", "");
      enqueueSnackbar("Copying is disabled for redacted documents.", {
        variant: "warning",
        preventDuplicate: true,
      });
    };
    const events = ["copy", "cut", "dragstart"];
    events.forEach((type) => document.addEventListener(type, block, true));
    return () =>
      events.forEach((type) =>
        document.removeEventListener(type, block, true),
      );
  }, [isRedacted, enqueueSnackbar]);

  // Load through the shared cache: a real Web Worker parses the file, range
  // requests fetch only what is needed, and reopened documents are instant.
  useEffect(() => {
    if (!url) return undefined;
    let active = true;
    setPdfDoc(getCachedPdf(url));
    setPageSizes([]);
    setLoadError(null);
    setRenderRange({ start: 1, end: 3, visibleStart: 1, visibleEnd: 1 });

    const { promise, release, onFail } = acquirePdf(url);
    promise.then(
      (pdf) => {
        if (!active) return;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
      },
      (err) => {
        if (active) setLoadError(err?.message || "Could not load PDF.");
      },
    );
    // The file's byte ranges stopped loading (e.g. the network dropped).
    onFail((err) => {
      if (!active) return;
      setPdfDoc(null);
      setLoadError(err?.message || "Could not load PDF.");
    });
    return () => {
      active = false;
      release();
    };
  }, [url, setNumPages]);

  // Measure/Probe page viewports dynamically (uses the same pdf proxy
  // react-pdf already loaded — no second fetch/parse of the file).
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    const measurePages = async () => {
      try {
        const firstPage = await pdfDoc.getPage(1);
        if (cancelled) return;
        const defaultViewport = firstPage.getViewport({ scale: 1 });
        const defaultSize = {
          width: defaultViewport.width,
          height: defaultViewport.height,
        };
        if (typeof setPageSize === "function") {
          setPageSize(defaultSize);
        }

        const sizes = new Array(pdfDoc.numPages).fill(defaultSize);
        setPageSizes(sizes);
      } catch (err) {
        if (!cancelled)
          setLoadError(err?.message || "Could not read PDF pages.");
      }
    };
    measurePages();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, setPageSize]);

  const pageLayout = useMemo(
    () => buildPageLayout(pageSizes, zoom),
    [pageSizes, zoom],
  );

  // Scroll-driven virtualization
  const computeRange = useCallback(() => {
    const container = scrollContainerRef?.current;
    const wrap = pdfWrapRef.current;
    if (!container || !wrap || !pageLayout.pages.length) return;

    if (singlePageView) {
      setRenderRange((prev) =>
        prev.start === currentPage && prev.end === currentPage
          ? prev
          : {
              start: currentPage,
              end: currentPage,
              visibleStart: currentPage,
              visibleEnd: currentPage,
            },
      );
      return;
    }

    const contRect = container.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const offsetTop = wrapRect.top - contRect.top + container.scrollTop;
    offsetTopRef.current = offsetTop;

    const relTop = Math.max(0, container.scrollTop - offsetTop);
    const relBottom = relTop + container.clientHeight;
    const firstVisible = findPageIndexAtOffset(pageLayout.pages, relTop);
    const lastVisible = findPageIndexAtOffset(pageLayout.pages, relBottom);
    const first = Math.max(1, firstVisible + 1 - RENDER_BUFFER);
    const last = Math.min(
      pageLayout.pages.length,
      lastVisible + 1 + RENDER_BUFFER,
    );

    const visibleStart = firstVisible + 1;
    const visibleEnd = lastVisible + 1;
    setRenderRange((prev) =>
      prev.start === first &&
      prev.end === last &&
      prev.visibleStart === visibleStart &&
      prev.visibleEnd === visibleEnd
        ? prev
        : { start: first, end: last, visibleStart, visibleEnd },
    );

    const activePage =
      findPageIndexAtOffset(
        pageLayout.pages,
        relTop + Math.min(container.clientHeight * 0.25, 180),
      ) + 1;

    if (isProgrammaticScroll && isProgrammaticScroll.current) return;
    setCurrentPage((prev) => (prev === activePage ? prev : activePage));
  }, [
    scrollContainerRef,
    pageLayout,
    setCurrentPage,
    isProgrammaticScroll,
    singlePageView,
    currentPage,
  ]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        computeRange();
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    computeRange();
    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [computeRange, scrollContainerRef]);

  // Recompute whenever page sizes or layouts are loaded/updated
  useEffect(() => {
    computeRange();
  }, [pdfDoc, pageLayout, computeRange]);

  // Jump to a specific page
  const scrollToPage = useCallback(
    (pageNum) => {
      const container = scrollContainerRef?.current;
      const page = pageLayout.pages[pageNum - 1];
      if (!container || !page) return;
      const target = singlePageView
        ? offsetTopRef.current
        : offsetTopRef.current + page.top;
      container.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
    },
    [scrollContainerRef, pageLayout, singlePageView],
  );

  useEffect(() => {
    // Wait for the measured layout as well; otherwise a restored page can be
    // consumed before its offset exists and the viewer falls back to page one.
    if (
      scrollTargetPage !== null &&
      pdfDoc &&
      pageLayout.pages[scrollTargetPage - 1]
    ) {
      scrollToPage(scrollTargetPage);
      onScrollTargetConsumed();
    }
  }, [
    scrollTargetPage,
    pdfDoc,
    pageLayout,
    scrollToPage,
    onScrollTargetConsumed,
  ]);

  // Single-page view mounts only the current page: pre-render its neighbours
  // into the bitmap cache at idle priority so Next/Previous draw at once.
  useEffect(() => {
    if (!singlePageView || !pdfDoc || !pageLayout.pages.length) {
      return undefined;
    }
    const tasks = [currentPage + 1, currentPage - 1]
      .filter((n) => pageLayout.pages[n - 1])
      .map((n) => {
        const task = renderPage({
          pdf: pdfDoc,
          docKey: url,
          pageNumber: n,
          cssWidth: pageLayout.pages[n - 1].width,
          priority: "idle",
        });
        task.promise.then(
          (result) => releaseSource(result.source),
          () => undefined,
        );
        return task;
      });
    return () => tasks.forEach((task) => task.cancel());
  }, [singlePageView, pdfDoc, url, currentPage, pageLayout]);

  // Text selection → annotation tip
  const handleMouseUp = useCallback(
    (e) => {
      if (noteMode || redactMode) return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (!text) return;
      const range = sel.getRangeAt(0);
      const container =
        range.commonAncestorContainer?.nodeType === 1
          ? range.commonAncestorContainer
          : range.commonAncestorContainer?.parentElement;
      const pageWrapper = container?.closest("[data-pdf-page]");
      const canvas = pageWrapper?.querySelector("canvas");
      const page = pageWrapper
        ? Number(pageWrapper.dataset.pdfPage)
        : currentPage;
      const { rects, canvasOffset } = getRectsRelativeToWrap(
        range,
        pdfWrapRef.current,
        canvas,
      );
      const viewportAnchor = getViewportAnchorFromRange(range);
      const canvasRect = canvas?.getBoundingClientRect();
      setPending({
        text,
        anchorPoint: viewportAnchor,
        rects,
        canvasOffset,
        page,
        pageWidth: canvasRect?.width,
        pageHeight: canvasRect?.height,
      });
    },
    [noteMode, redactMode, currentPage],
  );

  // Click → note pin or hyperlink placement
  const handleClick = useCallback(
    (e) => {
      if (redactMode) return;
      // Hyperlink mode takes priority
      if (hyperlinkMode) {
        if (e.target.closest("[data-hyperlinkpin]")) return;
        if (e.target.closest("[data-notepin]")) return;
        const pageWrapper = e.target.closest("[data-pdf-page]");
        if (!pageWrapper) return;
        const canvas = pageWrapper.querySelector("canvas");
        if (!canvas) return;
        const cb = canvas.getBoundingClientRect();
        const page = Number(pageWrapper.dataset.pdfPage);
        if (typeof onAddHyperlink === "function") {
          onAddHyperlink({
            x: e.clientX - cb.left,
            y: e.clientY - cb.top,
            page,
            cssScale: zoom / 100,
          });
        }
        return;
      }
      // Note mode
      if (!noteMode) return;
      if (e.target.closest("[data-notepin]")) return;
      const pageWrapper = e.target.closest("[data-pdf-page]");
      if (!pageWrapper) return;
      const canvas = pageWrapper.querySelector("canvas");
      if (!canvas) return;
      const cb = canvas.getBoundingClientRect();
      const page = Number(pageWrapper.dataset.pdfPage);
      setNotePending({
        x: e.clientX - cb.left,
        y: e.clientY - cb.top,
        anchorPoint: {
          x: e.clientX,
          y: e.clientY,
          left: e.clientX,
          bottom: e.clientY,
        },
        page,
      });
    },
    [noteMode, hyperlinkMode, redactMode, zoom, onAddHyperlink],
  );

  const saveAnnot = ({ comment, color }) => {
    onAddAnnot({
      id: uid(),
      text: pending.text,
      comment,
      color,
      page: pending.page || currentPage,
      rects: pending.rects,
      canvasOffset: pending.canvasOffset,
      cssScale: zoom / 100,
      time: new Date().toLocaleTimeString(),
    });
    setPending(null);
    window.getSelection().removeAllRanges();
  };

  const saveNote = (text) => {
    onAddNote({
      id: uid(),
      text,
      page: notePending.page || currentPage,
      x: notePending.x,
      y: notePending.y,
      cssScale: zoom / 100,
      time: new Date().toLocaleTimeString(),
    });
    setNotePending(null);
  };

  const redactSelection = () => {
    addRedactions(
      rectsToRedactions(
        pending.rects,
        pending.page || currentPage,
        pending.pageWidth,
        pending.pageHeight,
      ),
    );
    setPending(null);
    window.getSelection().removeAllRanges();
  };

  if (loadError) {
    return (
      <ErrorBox>
        Could not load PDF.{" "}
        <a href={url} target="_blank" rel="noopener noreferrer">
          Open directly
        </a>
      </ErrorBox>
    );
  }

  return (
    <>
      <PdfWrap
        ref={pdfWrapRef}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        $noteMode={noteMode}
        $hyperlinkMode={hyperlinkMode}
        $redactMode={redactMode}
      >
        <>
          {(!pdfDoc || pageLayout.pages.length === 0) && (
            <PdfPagePlaceholder
              width={Math.round((pageSize?.width || 595) * (zoom / 100))}
              height={Math.round((pageSize?.height || 842) * (zoom / 100))}
            />
          )}

          {pdfDoc && pageLayout.pages.length > 0 && (
            <PdfList
              style={{
                width: pageLayout.width,
                height: singlePageView
                  ? pageLayout.pages[currentPage - 1]?.height || 0
                  : pageLayout.height,
              }}
            >
              {Array.from(
                {
                  length: singlePageView
                    ? 1
                    : Math.max(0, renderRange.end - renderRange.start + 1),
                },
                (_, idx) => {
                  const pageNumber = singlePageView
                    ? currentPage
                    : renderRange.start + idx;
                  const page = pageLayout.pages[pageNumber - 1];
                  if (!page) return null;
                  return (
                    <div
                      key={`${url}-${pageNumber}`}
                      data-pdf-page={pageNumber}
                      style={{
                        position: "absolute",
                        top: singlePageView ? 0 : page.top,
                        left: (pageLayout.width - page.width) / 2,
                        width: page.width,
                        height: page.height,
                      }}
                    >
                      <VirtualPdfPage
                        pdf={pdfDoc}
                        docKey={url}
                        deferred={
                          !singlePageView &&
                          (pageNumber < renderRange.visibleStart ||
                            pageNumber > renderRange.visibleEnd)
                        }
                        pageNumber={pageNumber}
                        zoom={zoom}
                        width={page.width}
                        height={page.height}
                        annotations={annotations}
                        notes={notes}
                        onDeleteAnnot={onDeleteAnnot}
                        onDeleteNote={onDeleteNote}
                        hyperlinks={hyperlinks}
                        onOpenHyperlink={onOpenHyperlink}
                        onDeleteHyperlink={onDeleteHyperlink}
                        redactions={redactions}
                        redactMode={redactMode}
                        onAddRedactions={addRedactions}
                        onDeleteRedaction={deleteRedaction}
                      />
                    </div>
                  );
                },
              )}
            </PdfList>
          )}
        </>
      </PdfWrap>

      {pending && !noteMode && !redactMode && (
        <SelectionTip
          anchorPoint={pending.anchorPoint}
          text={pending.text}
          onRedact={pending.pageWidth ? redactSelection : undefined}
          onSave={saveAnnot}
          onCancel={() => {
            setPending(null);
            window.getSelection().removeAllRanges();
          }}
        />
      )}
      {notePending && (
        <NoteInputTip
          anchorPoint={notePending.anchorPoint}
          page={currentPage}
          onSave={saveNote}
          onCancel={() => setNotePending(null)}
        />
      )}
    </>
  );
}

// ─── PdfAnnotOverlay ─────────────────────────────────────────────────────────

const PdfAnnotOverlay = React.memo(function PdfAnnotOverlay({
  ann,
  color,
  zoom,
}) {
  const factor = zoom / 100 / (ann.cssScale || 1);
  return (
    <>
      {ann.rects.map((r, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: r.left * factor,
            top: r.top * factor,
            width: r.width * factor,
            height: r.height * factor,
            background: color.alpha,
            borderBottom: `2px solid ${color.bg}`,
            pointerEvents: "none",
            mixBlendMode: "multiply",
            borderRadius: 2,
          }}
        />
      ))}
    </>
  );
});

// ─── DocxViewer ───────────────────────────────────────────────────────────────

function DocxViewer({
  url,
  annotations,
  notes,
  onAddAnnot,
  onDeleteAnnot,
  onAddNote,
  onDeleteNote,
  noteMode,
}) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const [notePending, setNotePending] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setHtml("");
    fetchDocxHtml(url)
      .then((h) => {
        setHtml(h);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [url]);

  const handleMouseUp = useCallback(() => {
    if (noteMode) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const text = sel.toString().trim();
    if (!text) return;
    const range = sel.getRangeAt(0);
    const viewportAnchor = getViewportAnchorFromRange(range);
    setPending({ text, anchorPoint: viewportAnchor });
  }, [noteMode]);

  const handleClick = useCallback(
    (e) => {
      if (!noteMode) return;
      if (e.target.closest("[data-notepin]")) return;
      const wb = wrapRef.current?.getBoundingClientRect();
      if (!wb) return;
      setNotePending({
        x: e.clientX - wb.left,
        y: e.clientY - wb.top,
        anchorPoint: {
          x: e.clientX,
          y: e.clientY,
          left: e.clientX,
          bottom: e.clientY,
        },
      });
    },
    [noteMode],
  );

  const saveAnnot = ({ comment, color }) => {
    onAddAnnot({
      id: uid(),
      text: pending.text,
      comment,
      color,
      page: null,
      rects: [],
      canvasOffset: { left: 0, top: 0 },
      cssScale: 1,
      time: new Date().toLocaleTimeString(),
    });
    setPending(null);
    window.getSelection().removeAllRanges();
  };

  const saveNote = (text) => {
    onAddNote({
      id: uid(),
      text,
      page: null,
      x: notePending.x,
      y: notePending.y,
      cssScale: 1,
      time: new Date().toLocaleTimeString(),
    });
    setNotePending(null);
  };

  // 👇 IMPORTANT: this hook MUST stay here, before any `if (...) return`
  const renderedHtml = useMemo(() => {
    let out = html;
    annotations.forEach((ann) => {
      const color =
        HIGHLIGHT_COLORS.find((c) => c.key === ann.color) ||
        HIGHLIGHT_COLORS[0];
      try {
        const escaped = ann.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        out = out.replace(
          new RegExp(escaped, "g"),
          `<mark data-ann-id="${ann.id}" style="background:${color.alpha};border-bottom:2px solid ${color.bg};border-radius:2px;padding:0 1px;cursor:pointer;">${ann.text}</mark>`,
        );
      } catch {}
    });
    return out;
  }, [html, annotations]);

  // Early returns come AFTER all hooks
  if (loading)
    return (
      <DocxSkeletonWrap>
        <SkeletonShimmer />
        <DocxSkeletonInner>
          {[70, 100, 95, 88, 60, 100, 96, 80, 75, 55].map((w, i) => (
            <SkeletonBar
              key={i}
              style={{ width: `${w}%`, height: 12, marginBottom: 12 }}
            />
          ))}
        </DocxSkeletonInner>
      </DocxSkeletonWrap>
    );

  if (error)
    return (
      <ErrorBox>
        Could not load DOCX: {error}
        <br />
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download
        </a>
      </ErrorBox>
    );

  const docxNotes = notes.filter((n) => n.page === null && n.x != null);

  return (
    <>
      <DocxScrollArea>
        <div style={{ position: "relative", width: "100%", maxWidth: 820 }}>
          <DocxContent
            ref={wrapRef}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
            onMouseUp={handleMouseUp}
            onClick={handleClick}
            $noteMode={noteMode}
          />
          {docxNotes.map((n) => (
            <div
              key={n.id}
              data-notepin="1"
              style={{ position: "absolute", left: n.x, top: n.y, zIndex: 50 }}
            >
              <StickyNotePin note={n} zoom={100} onDelete={onDeleteNote} />
            </div>
          ))}
        </div>
      </DocxScrollArea>

      {pending && !noteMode && (
        <SelectionTip
          anchorPoint={pending.anchorPoint}
          text={pending.text}
          onSave={saveAnnot}
          onCancel={() => {
            setPending(null);
            window.getSelection().removeAllRanges();
          }}
        />
      )}
      {notePending && (
        <NoteInputTip
          anchorPoint={notePending.anchorPoint}
          page={null}
          onSave={saveNote}
          onCancel={() => setNotePending(null)}
        />
      )}
    </>
  );
}

function ImageViewer({ url, name }) {
  return (
    <ImageViewerWrap>
      <img
        src={url}
        alt={name}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          borderRadius: 4,
          boxShadow:
            "0 2px 4px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.12), 0 20px 48px rgba(0,0,0,0.08)",
        }}
      />
    </ImageViewerWrap>
  );
}

function VideoViewer({ url, name }) {
  return (
    <MediaViewerWrap>
      <VideoCard>
        <video
          src={url}
          controls
          style={{
            width: "100%",
            maxHeight: "70vh",
            borderRadius: 8,
            outline: "none",
          }}
          controlsList="nodownload"
        />
        <MediaTitle>{name}</MediaTitle>
      </VideoCard>
    </MediaViewerWrap>
  );
}

function AudioViewer({ url, name }) {
  return (
    <MediaViewerWrap>
      <AudioCard>
        <AudioDisc>{"🎵"}</AudioDisc>
        <AudioTitle>{name}</AudioTitle>
        <audio
          src={url}
          controls
          style={{ width: "100%", maxWidth: "360px" }}
          controlsList="nodownload"
        />
      </AudioCard>
    </MediaViewerWrap>
  );
}

function TxtViewer({ url }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((t) => {
        setText(t);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [url]);

  if (loading)
    return (
      <DocxSkeletonWrap>
        <SkeletonShimmer />
      </DocxSkeletonWrap>
    );
  if (error)
    return (
      <ErrorBox>
        Could not load file: {error}
        <br />
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download
        </a>
      </ErrorBox>
    );

  return (
    <TxtContent>
      <pre>{text}</pre>
    </TxtContent>
  );
}

function OfficeViewer({ url }) {
  const viewerSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  return (
    <OfficeFrameWrap>
      <iframe
        title="office-preview"
        src={viewerSrc}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    </OfficeFrameWrap>
  );
}

// ─── DocHyperlinkItem ─────────────────────────────────────────────────────────

const DocHyperlinkItem = React.memo(function DocHyperlinkItem({
  hyperlink,
  onOpen,
  onDelete,
  onUpdateNote,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftNote, setDraftNote] = useState(hyperlink.note || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setDraftNote(hyperlink.note || "");
  }, [hyperlink.note]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleStartEdit = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setDraftNote(hyperlink.note || "");
    setIsEditing(true);
  };

  const handleSave = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    const finalVal = (draftNote || "").slice(0, 30);
    if (typeof onUpdateNote === "function") {
      onUpdateNote(hyperlink.id, finalVal);
    }
    setIsEditing(false);
  };

  const handleCancel = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setDraftNote(hyperlink.note || "");
    setIsEditing(false);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    if (val.length <= 30) {
      setDraftNote(val);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave(e);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel(e);
    }
  };

  return (
    <DocHyperlinkCard key={hyperlink.id}>
      <DocHyperlinkTopRow>
        <DocHyperlinkIconWrap>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </DocHyperlinkIconWrap>
        <DocHyperlinkInfo>
          <DocHyperlinkTarget
            title={hyperlink.targetDoc?.name}
            onClick={() =>
              hyperlink.targetDoc &&
              typeof onOpen === "function" &&
              onOpen(
                hyperlink.targetDoc,
                hyperlink.targetPage || hyperlink.targetDoc.targetPage,
              )
            }
          >
            {hyperlink.targetDoc?.name || "Unknown Document"}
          </DocHyperlinkTarget>
          {hyperlink.time && (
            <AnnotFooter>
              <FooterTime style={{ marginLeft: 0 }}>
                {hyperlink.time}
              </FooterTime>
            </AnnotFooter>
          )}
        </DocHyperlinkInfo>
        <DeleteBtn
          onClick={() =>
            typeof onDelete === "function" && onDelete(hyperlink.id)
          }
          title="Delete hyperlink"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </DeleteBtn>
      </DocHyperlinkTopRow>

      <DocHyperlinkNoteSection>
        {isEditing ? (
          <DocHyperlinkNoteInputWrap
            onClick={(e) => {
              if (e && e.stopPropagation) {
                e.stopPropagation();
              }
            }}
          >
            <DocHyperlinkNoteInputRow>
              <DocHyperlinkNoteInput
                ref={inputRef}
                value={draftNote}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Why is this document linked?"
                maxLength={30}
              />
            </DocHyperlinkNoteInputRow>
            <DocHyperlinkNoteMetaRow>
              <DocHyperlinkCharCount $nearLimit={draftNote.length >= 25}>
                {draftNote.length}/30
              </DocHyperlinkCharCount>
              <DocHyperlinkNoteActions>
                <DocHyperlinkNoteCancelBtn type="button" onClick={handleCancel}>
                  Cancel
                </DocHyperlinkNoteCancelBtn>
                <DocHyperlinkNoteSaveBtn type="button" onClick={handleSave}>
                  Save
                </DocHyperlinkNoteSaveBtn>
              </DocHyperlinkNoteActions>
            </DocHyperlinkNoteMetaRow>
          </DocHyperlinkNoteInputWrap>
        ) : hyperlink.note ? (
          <DocHyperlinkNoteDisplay
            onClick={handleStartEdit}
            title="Click to edit note"
          >
            <DocHyperlinkNoteTextWrap>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke={G.teal}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              <DocHyperlinkNoteText title={hyperlink.note}>
                {hyperlink.note}
              </DocHyperlinkNoteText>
            </DocHyperlinkNoteTextWrap>
            <DocHyperlinkNoteEditBtn
              type="button"
              onClick={handleStartEdit}
              title="Edit note"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </DocHyperlinkNoteEditBtn>
          </DocHyperlinkNoteDisplay>
        ) : (
          <DocHyperlinkAddNoteBtn type="button" onClick={handleStartEdit}>
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Note
          </DocHyperlinkAddNoteBtn>
        )}
      </DocHyperlinkNoteSection>
    </DocHyperlinkCard>
  );
});

// ─── RightSidePanel ───────────────────────────────────────────────────────────

const RightSidePanel = React.memo(function RightSidePanel({
  annotations,
  notes,
  onDeleteAnnot,
  onDeleteNote,
  visibility,
  onChangeVisibility,
  doc,
  numPages,
  ft,
  hideVisibility,
  docHyperlinks,
  onDeleteDocHyperlink,
  onOpenDocHyperlink,
  onUpdateDocHyperlinkNote,
}) {
  const [activeTab, setActiveTab] = useState("annotations_notes");
  const [expandedSummary, setExpandedSummary] = useState(false);
  const [expandedKeywords, setExpandedKeywords] = useState(false);
  const keywordsRef = useRef(null);
  const summaryRef = useRef(null);

  const scrollToSection = (ref) => {
    setTimeout(() => {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }, 110);
  };

  const summaryText = doc.documentSummary || "";
  const keywordsRaw = doc.keywords || [""];
  const keywordsText = Array.isArray(keywordsRaw)
    ? keywordsRaw.join(", ")
    : keywordsRaw;

  const isSummaryLong = summaryText.length > 180;
  const isKeywordsLong = keywordsText.length > 120;

  const formattedSummary = (summaryText || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  const rawHyperlinks = useMemo(() => {
    const list =
      doc?.hyperlinks ||
      doc?.links ||
      doc?.externalLinks ||
      doc?.properties?.hyperlinks ||
      doc?.properties?.links ||
      [];
    return Array.isArray(list)
      ? list
      : typeof list === "object" && list
        ? Object.values(list)
        : [];
  }, [doc]);

  return (
    <RightPanelWrap>
      <PanelTabStrip>
        <PanelTabBtn
          $active={activeTab === "annotations_notes"}
          onClick={() => setActiveTab("annotations_notes")}
        >
          Annotations & Notes
          {annotations.length + notes.length > 0 && (
            <PanelTabBadge $active={activeTab === "annotations_notes"}>
              {annotations.length + notes.length}
            </PanelTabBadge>
          )}
        </PanelTabBtn>
        <PanelTabBtn
          $active={activeTab === "properties"}
          onClick={() => setActiveTab("properties")}
        >
          Properties
        </PanelTabBtn>
        <PanelTabBtn
          $active={activeTab === "hyperlinks"}
          onClick={() => setActiveTab("hyperlinks")}
        >
          Hyperlinks
          {rawHyperlinks.length + (docHyperlinks || []).length > 0 && (
            <PanelTabBadge $active={activeTab === "hyperlinks"}>
              {rawHyperlinks.length + (docHyperlinks || []).length}
            </PanelTabBadge>
          )}
        </PanelTabBtn>
      </PanelTabStrip>

      <RightPanelScroll>
        {activeTab === "annotations_notes" && (
          <>
            <SectionBlock>
              <SectionHeader>
                <SectionTitle>Annotations</SectionTitle>
                {annotations.length > 0 && (
                  <SectionBadge $color="#00818a">
                    {annotations.length}
                  </SectionBadge>
                )}
              </SectionHeader>
              {annotations.length === 0 ? (
                <EmptyState>
                  <EmptyTitle>No annotations yet</EmptyTitle>
                </EmptyState>
              ) : (
                <CardList>
                  {annotations.map((a) => {
                    const c =
                      HIGHLIGHT_COLORS.find((x) => x.key === a.color) ||
                      HIGHLIGHT_COLORS[0];
                    return (
                      <AnnotCard key={a.id}>
                        <AnnotColorStrip style={{ background: c.bg }} />
                        <AnnotCardBody>
                          <AnnotCardTop>
                            <AnnotSnippet>
                              "{a.text.slice(0, 60)}
                              {a.text.length > 60 ? "…" : ""}"
                            </AnnotSnippet>
                            <DeleteBtn
                              onClick={() => onDeleteAnnot(a.id)}
                              title="Delete"
                            >
                              <svg
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </DeleteBtn>
                          </AnnotCardTop>
                          {a.comment && (
                            <AnnotComment>{a.comment}</AnnotComment>
                          )}
                          <AnnotFooter>
                            <FooterChip>
                              {a.page ? `Pg ${a.page}` : "DOCX"}
                            </FooterChip>
                            <FooterChip $color={c.bg}>{c.label}</FooterChip>
                            {a.time && <FooterTime>{a.time}</FooterTime>}
                          </AnnotFooter>
                        </AnnotCardBody>
                      </AnnotCard>
                    );
                  })}
                </CardList>
              )}
            </SectionBlock>

            <SectionDivider />

            <SectionBlock>
              <SectionHeader>
                <SectionTitle>Notes</SectionTitle>
                {notes.length > 0 && (
                  <SectionBadge $color="#B45309">{notes.length}</SectionBadge>
                )}
              </SectionHeader>
              {notes.length === 0 ? (
                <EmptyState>
                  <EmptyTitle>No notes yet</EmptyTitle>
                </EmptyState>
              ) : (
                <CardList>
                  {notes.map((n) => (
                    <NoteCard key={n.id}>
                      <NoteCardTop>
                        <NoteCardMeta>
                          <FooterChip $yellow>
                            {n.page ? `Pg ${n.page}` : "Document"}
                          </FooterChip>
                          {n.time && <FooterTime>{n.time}</FooterTime>}
                        </NoteCardMeta>
                        <DeleteBtn
                          onClick={() => onDeleteNote(n.id)}
                          title="Delete"
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </DeleteBtn>
                      </NoteCardTop>
                      <NoteCardText>{n.text}</NoteCardText>
                    </NoteCard>
                  ))}
                </CardList>
              )}
            </SectionBlock>
          </>
        )}

        {activeTab === "properties" && (
          <>
            <SectionBlock>
              <SectionHeader>
                <SectionTitle>Properties</SectionTitle>
              </SectionHeader>
              <InfoList>
                <InfoItem>
                  <InfoKey>Name</InfoKey>
                  <InfoVal title={doc.name}>
                    {doc.name.length > 32
                      ? doc.name.slice(0, 32) + "…"
                      : doc.name}
                  </InfoVal>
                </InfoItem>
                {getExhibitId(doc) && (
                  <InfoItem>
                    <InfoKey>Exhibit ID</InfoKey>
                    <InfoVal>{getExhibitId(doc)}</InfoVal>
                  </InfoItem>
                )}
                {Array.isArray(keywordsRaw) ? (
                  <InfoItem ref={keywordsRef}>
                    <InfoKey>Keywords</InfoKey>
                    <div>
                      {keywordsRaw.map((kw, idx) => (
                        <TruncatedText key={idx} $expanded={expandedKeywords}>
                          {isKeywordsLong && !expandedKeywords
                            ? kw.slice(0, 120) + "…"
                            : kw}
                        </TruncatedText>
                      ))}
                      {isKeywordsLong && (
                        <ExpandLink
                          onClick={() => {
                            setExpandedKeywords((p) => {
                              const n = !p;
                              if (!p) scrollToSection(keywordsRef);
                              return n;
                            });
                          }}
                        >
                          {expandedKeywords ? "Show less" : "Show all"}
                        </ExpandLink>
                      )}
                    </div>
                  </InfoItem>
                ) : (
                  <InfoItem ref={keywordsRef}>
                    <InfoKey>Keywords</InfoKey>
                    <div>
                      <TruncatedText $expanded={expandedKeywords}>
                        {isKeywordsLong && !expandedKeywords
                          ? keywordsText.slice(0, 120) + "…"
                          : keywordsText}
                      </TruncatedText>
                      {isKeywordsLong && (
                        <ExpandLink
                          onClick={() => {
                            setExpandedKeywords((p) => {
                              const n = !p;
                              if (!p) scrollToSection(keywordsRef);
                              return n;
                            });
                          }}
                        >
                          {expandedKeywords ? "Show less" : "Show all"}
                        </ExpandLink>
                      )}
                    </div>
                  </InfoItem>
                )}
                {summaryText && (
                  <InfoItem ref={summaryRef}>
                    <InfoKey>Summary</InfoKey>
                    <div>
                      <TruncatedText $expanded={expandedSummary}>
                        {isSummaryLong && !expandedSummary
                          ? formattedSummary.slice(0, 180) + "..."
                          : formattedSummary}
                      </TruncatedText>
                      {isSummaryLong && (
                        <ExpandLink
                          onClick={() => {
                            setExpandedSummary((p) => {
                              const n = !p;
                              if (!p) scrollToSection(summaryRef);
                              return n;
                            });
                          }}
                        >
                          {expandedSummary ? "Show less" : "Read full summary"}
                        </ExpandLink>
                      )}
                    </div>
                  </InfoItem>
                )}
                <InfoItem>
                  <InfoKey>Format</InfoKey>
                  <InfoVal>
                    <FormatPill $ft={ft}>{ft.toUpperCase()}</FormatPill>
                  </InfoVal>
                </InfoItem>
                {numPages && (
                  <InfoItem>
                    <InfoKey>Pages</InfoKey>
                    <InfoVal>{numPages}</InfoVal>
                  </InfoItem>
                )}
                <InfoItem>
                  <InfoKey>Annotations</InfoKey>
                  <InfoVal>
                    <StatBadge $color="#00818a">{annotations.length}</StatBadge>
                  </InfoVal>
                </InfoItem>
                <InfoItem>
                  <InfoKey>Notes</InfoKey>
                  <InfoVal>
                    <StatBadge $color="#B45309">{notes.length}</StatBadge>
                  </InfoVal>
                </InfoItem>
              </InfoList>
            </SectionBlock>

            {doc?.bundle || hideVisibility ? null : doc?.hideVisibility ? (
              <>
                <SectionDivider />
                <SectionBlock>
                  <SectionHeader>
                    <SectionTitle>Visibility</SectionTitle>
                  </SectionHeader>
                  <ShareDesc>Control who can view this document.</ShareDesc>
                  <VisOptionList>
                    {VIS_LEVELS.map((lvl) => (
                      <VisOptionCard
                        key={lvl.key}
                        $active={visibility === lvl.key}
                        onClick={() => onChangeVisibility(lvl.key)}
                      >
                        <VisIconCircle style={{ background: lvl.bg }}>
                          <span style={{ fontSize: 15 }}>{lvl.icon}</span>
                        </VisIconCircle>
                        <VisOptionText>
                          <VisOptionName>{lvl.name}</VisOptionName>
                          <VisOptionDesc>{lvl.desc}</VisOptionDesc>
                        </VisOptionText>
                        <VisRadioCircle $active={visibility === lvl.key}>
                          {visibility === lvl.key && <VisRadioDot />}
                        </VisRadioCircle>
                      </VisOptionCard>
                    ))}
                  </VisOptionList>
                </SectionBlock>
              </>
            ) : null}
          </>
        )}

        {activeTab === "hyperlinks" && (
          <SectionBlock>
            {/* ── User-created document hyperlinks ── */}
            {(docHyperlinks || []).length > 0 && (
              <>
                <SectionHeader>
                  <SectionTitle>Hyperlinks</SectionTitle>
                  <SectionBadge $color="#007aff">
                    {(docHyperlinks || []).length}
                  </SectionBadge>
                </SectionHeader>
                <CardList style={{ marginBottom: 16 }}>
                  {(docHyperlinks || []).map((hl) => (
                    <DocHyperlinkItem
                      key={hl.id}
                      hyperlink={hl}
                      onOpen={onOpenDocHyperlink}
                      onDelete={onDeleteDocHyperlink}
                      onUpdateNote={onUpdateDocHyperlinkNote}
                    />
                  ))}
                </CardList>
                {rawHyperlinks.length > 0 && <SectionDivider />}
              </>
            )}

            {/* ── External hyperlinks (from document data) ── */}
            <SectionHeader>
              <SectionTitle></SectionTitle>
              {rawHyperlinks.length > 0 && (
                <SectionBadge $color="#007aff">
                  {rawHyperlinks.length}
                </SectionBadge>
              )}
            </SectionHeader>
            {rawHyperlinks.length === 0 &&
            (docHyperlinks || []).length === 0 ? (
              <EmptyState>
                <EmptyTitle>No hyperlinks yet</EmptyTitle>
              </EmptyState>
            ) : rawHyperlinks.length === 0 ? null : (
              <CardList>
                {rawHyperlinks.map((link, idx) => {
                  const url =
                    typeof link === "string"
                      ? link
                      : link.url || link.href || link.link || "";
                  const title =
                    typeof link === "string"
                      ? link
                      : link.title ||
                        link.name ||
                        link.label ||
                        link.text ||
                        url;
                  const page =
                    typeof link === "object" ? link.page || link.pageNum : null;
                  return (
                    <HyperlinkCard
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <HyperlinkIconWrap>
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                      </HyperlinkIconWrap>
                      <HyperlinkInfo>
                        <HyperlinkTitle title={title}>{title}</HyperlinkTitle>
                        {url && url !== title && (
                          <HyperlinkUrl title={url}>{url}</HyperlinkUrl>
                        )}
                        {page && <FooterChip>Pg {page}</FooterChip>}
                      </HyperlinkInfo>
                      <HyperlinkExternalIcon>
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </HyperlinkExternalIcon>
                    </HyperlinkCard>
                  );
                })}
              </CardList>
            )}
          </SectionBlock>
        )}
      </RightPanelScroll>
    </RightPanelWrap>
  );
});

// ─── DownloadMenu ─────────────────────────────────────────────────────────────

function DownloadMenu({ open, doc, annotations, notes, onClose, isPdf }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [redactLoading, setRedactLoading] = useState(false);
  const [redactions] = useRedactions(doc?.s3url);
  if (!open) return null;
  const dlOriginal = () => {
    const a = document.createElement("a");
    a.href = doc.s3url;
    a.download = doc.name;
    a.target = "_blank";
    a.click();
    onClose();
  };
  const dlAnnotatedPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadPdfWithAnnotations(
        doc.s3url,
        annotations,
        notes,
        doc.name,
        redactions,
      );
    } catch (e) {
      alert("Could not generate annotated PDF: " + e.message);
    }
    setPdfLoading(false);
    onClose();
  };
  const dlRedactedPdf = async () => {
    setRedactLoading(true);
    try {
      const blob = await buildRedactedPdf(doc.s3url, redactions);
      downloadBlob(blob, redactedFileName(doc.name));
    } catch (e) {
      alert("Could not generate redacted PDF: " + e.message);
    }
    setRedactLoading(false);
    onClose();
  };
  return (
    <DlDropdown>
      <DlItem onClick={dlOriginal}>
        <div>
          <DlItemTitle>Original document</DlItemTitle>
          <DlItemSub>Download unmodified file</DlItemSub>
        </div>
      </DlItem>
      {isPdf && (
        <DlItem onClick={dlAnnotatedPdf} disabled={pdfLoading}>
          <div>
            <DlItemTitle>
              {pdfLoading ? "Generating…" : "PDF with highlights & notes"}
            </DlItemTitle>
            <DlItemSub>
              {redactions.length
                ? "Bakes annotations and redactions into the PDF"
                : "Bakes all annotations into the PDF"}
            </DlItemSub>
          </div>
        </DlItem>
      )}
      {isPdf && (
        <DlItem
          onClick={dlRedactedPdf}
          disabled={redactLoading || !redactions.length}
        >
          <div>
            <DlItemTitle>
              {redactLoading ? "Redacting…" : "Redacted PDF"}
            </DlItemTitle>
            <DlItemSub>
              {redactions.length
                ? `${redactions.length} redaction${redactions.length === 1 ? "" : "s"} · hidden text is removed`
                : "No redactions yet. Use the Redact tool first."}
            </DlItemSub>
          </div>
        </DlItem>
      )}
    </DlDropdown>
  );
}

const getOwnerBadgeStyle = (ownerType) => {
  const type = String(ownerType || "Neutral").toLowerCase();
  if (type.includes("claimant")) return { bg: "#e6fffa", color: "#0d9488" }; // teal
  if (type.includes("respondent")) return { bg: "#eff6ff", color: "#2563eb" }; // blue
  if (type.includes("manager") || type.includes("casemanager"))
    return { bg: "#faf5ff", color: "#7c3aed" }; // purple
  return { bg: "#f1f5f9", color: "#475569" }; // neutral
};

const getOwnerRoleLabel = (ownerType) => {
  const type = String(ownerType || "").toLowerCase();
  if (type.includes("claimant")) return "Claimant";
  if (type.includes("respondent")) return "Respondent";
  if (type.includes("manager") || type.includes("casemanager"))
    return "Case Manager";
  if (type.includes("mediator")) return "Mediator";
  if (type.includes("arbitrator")) return "Arbitrator";
  return ownerType || "Neutral";
};

const getVisibilityBadgeStyle = (type) => {
  const t = String(type || "me").toLowerCase();
  if (t === "me") return { label: "Only Me", bg: "#fef2f2", color: "#dc2626" }; // red
  if (t === "strictmode")
    return { label: "Strict Mode", bg: "#fff7ed", color: "#ea580c" }; // orange
  if (t === "partyname")
    return { label: "Visible to Parties", bg: "#ecfdf5", color: "#059669" }; // green
  if (t === "all")
    return { label: "Visible to All", bg: "#f0fdf4", color: "#16a34a" }; // light green
  return { label: type || "Only Me", bg: "#f1f5f9", color: "#475569" };
};

const parsePageCount = (pages) => {
  if (pages == null) return 0;
  const str = String(pages).trim();
  if (!str) return 0;
  return str.split(",").reduce((total, part) => {
    const segment = part.trim();
    if (!segment) return total;
    const rangeMatch = segment.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
    if (rangeMatch) {
      const start = Number(rangeMatch[1]);
      const end = Number(rangeMatch[2]);
      if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start)
        return total + (end - start + 1);
    }
    const singleVal = Number(segment);
    if (!Number.isNaN(singleVal)) return total + 1;
    return total;
  }, 0);
};

// ─── Main DocumentViewer ──────────────────────────────────────────────────────

export default function DocumentViewer({
  doc,
  onBack,
  openTabs,
  onTabClick,
  onTabClose,
  id,
  setTrigger,
  fetchAllBundles,
  activeSection,
  trialBundles = [],
  onCompareToggle,
  onVisibilityChange,
  onPrevDoc,
  onNextDoc,
  hasPrevDoc = false,
  hasNextDoc = false,
  onOpenLinkedDoc,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(90);
  const [rightOpen, setRightOpen] = useState(false);
  const [annotations, setAnnotations] = useState(() => loadAnnotations(doc.id));
  const [notes, setNotes] = useState(() => loadNotes(doc.id));
  const [noteMode, setNoteMode] = useState(false);
  const [hyperlinks, setHyperlinks] = useState(() => loadHyperlinks(doc.id));
  const [hyperlinkMode, setHyperlinkMode] = useState(false);
  const [redactMode, setRedactMode] = useState(false);
  const [mainRedactions, setMainRedactions] = useRedactions(doc.s3url);
  const [hyperlinkPending, setHyperlinkPending] = useState(null);
  const [showHyperlinkDocModal, setShowHyperlinkDocModal] = useState(false);
  const [hyperlinkSearchQuery, setHyperlinkSearchQuery] = useState("");
  const [selectedHlDoc, setSelectedHlDoc] = useState(null);
  const [hlTargetPage, setHlTargetPage] = useState("");
  const [hlPageError, setHlPageError] = useState("");
  const [scrollTargetPage, setScrollTargetPage] = useState(null);
  const [singlePageView, setSinglePageView] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { activeDoc } = useLoader();
  const isProgrammaticScrollRef = useRef(false);
  const suppressAutoFitRef = useRef(false);
  const [visibility, setVisibility] = useState(doc?.visibilityType || "me");

  useEffect(() => {
    setVisibility(doc?.visibilityType || "me"); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.visibilityType]);

  const [pageSize, setPageSize] = useState({ width: 595, height: 842 });
  const [comparePageSize, setComparePageSize] = useState({
    width: 595,
    height: 842,
  });

  useEffect(() => {
    setPageInputValue(currentPage);
  }, [currentPage]);

  // Comparison mode states
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareDoc, setCompareDoc] = useState(null);
  const [mainDocOverride, setMainDocOverride] = useState(null);
  const [selectCompareSide, setSelectCompareSide] = useState("compare");
  const [activeCompareSide, setActiveCompareSide] = useState("original");
  const [compareAnnotations, setCompareAnnotations] = useState([]);
  const [compareNotes, setCompareNotes] = useState([]);

  const effectiveMainDoc =
    compareOpen && compareDoc && mainDocOverride ? mainDocOverride : doc;

  useEffect(() => {
    if (compareOpen && compareDoc) {
      setActiveCompareSide("original");
    }
  }, [compareOpen, compareDoc, doc]);

  useEffect(() => {
    if (compareDoc?.id) {
      setCompareAnnotations(loadAnnotations(compareDoc.id));
      setCompareNotes(loadNotes(compareDoc.id));
    } else {
      setCompareAnnotations([]);
      setCompareNotes([]);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id]);

  useEffect(() => {
    if (compareDoc?.id) {
      saveAnnotations(compareDoc.id, compareAnnotations);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareAnnotations]);

  useEffect(() => {
    if (compareDoc?.id) {
      saveNotes(compareDoc.id, compareNotes);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareNotes]);

  useEffect(() => {
    if (!doc?.s3url) return;
    let link;
    try {
      const origin = new URL(doc.s3url).origin;
      link = document.createElement("link");
      link.rel = "preconnect";
      link.href = origin;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    } catch {}
    return () => {
      if (link) document.head.removeChild(link);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.s3url]);

  const [coreCompareDocs, setCoreCompareDocs] = useState([]);
  const [loadingCompareDocs, setLoadingCompareDocs] = useState(false);
  const [comparePage, setComparePage] = useState(1);
  const [compareNumPages, setCompareNumPages] = useState(null);
  const [compareZoom, setCompareZoom] = useState(90);
  const [compareScrollTargetPage, setCompareScrollTargetPage] = useState(null);
  const [compareSinglePageView, setCompareSinglePageView] = useState(false);
  const [comparePageInputValue, setComparePageInputValue] = useState(1);
  useEffect(() => {
    setComparePageInputValue(comparePage);
  }, [comparePage]);
  const [showSelectCompareModal, setShowSelectCompareModal] = useState(false);
  const [compareSearchQuery, setCompareSearchQuery] = useState("");
  const [wasRightPanelOpenBeforeCompare, setWasRightPanelOpenBeforeCompare] =
    useState(false);
  const compareCanvasRef = useRef(null);
  const isCompareProgrammaticScrollRef = useRef(false);

  const dlRef = useRef(null);
  const zoomRef = useRef(null);
  const appRef = useRef(null);
  const canvasRef = useRef(null); // scroll container
  const zoomAnchorRef = useRef(null);
  // Tracks whether the user has manually set a zoom level.
  // When true, the ResizeObserver will not override zoom with auto-fit.
  const isUserZoomedRef = useRef(false);

  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  const ft = detectFileType(doc.s3url);
  const isPdf = ft === "pdf";
  const isDocx = ft === "docx"; // only true .docx goes through mammoth now
  const isLegacyDoc = ft === "doc"; // old binary .doc → Office Online viewer instead
  const isImage = ft === "image";
  const isTxt = ft === "txt";
  const isOffice = ft === "office";
  const isVideo = ft === "video";
  const isAudio = ft === "audio";
  const documentPagePositionKey = getDocumentPagePositionKey(doc);

  // Save the current reading position before this document is replaced (or the
  // viewer unmounts). Effect cleanup uses the values from the correct document.
  useEffect(() => {
    if (!isPdf || !documentPagePositionKey) return undefined;
    return () => {
      documentPagePositions.set(documentPagePositionKey, {
        page: currentPage,
        zoom,
        singlePageView,
      });
    };
  }, [currentPage, documentPagePositionKey, isPdf, singlePageView, zoom]);

  // Fetch all Core Bundle PDFs for comparison
  const fetchAllCoreDocs = useCallback(async () => {
    if (!id) return;
    setLoadingCompareDocs(true);
    try {
      const res = await CaseService.getcaseDocuments(
        id,
        "?page=1&perPage=1000",
      );
      if (res?.data && Array.isArray(res.data)) {
        const pdfDocs = res.data
          .map((d) => {
            const rawPages =
              d.pageCount ||
              d.pages ||
              d.totalPages ||
              d.page_count ||
              d.totalPageCount;
            const tabNum = d.tabNum ?? d.tab_num ?? d.tabNumber ?? null;
            const rawDocNo =
              d.documentNo ??
              d.document_no ??
              d.documentNumber ??
              d.docNo ??
              d.doc_no ??
              "";
            const rawTabRef =
              d.tabRef ||
              d.tab_ref ||
              d.tabRefNumber ||
              d.refNumber ||
              d.ref ||
              d.reference ||
              d.display_tab ||
              d.displayTab ||
              rawDocNo ||
              "";
            const tabRef = rawTabRef;
            const refNumber = rawDocNo || rawTabRef;
            return {
              id: d.id,
              name: d.name,
              s3url: d.url?.url || d.fileUrl || d.url,
              source: d.ownerType || "Neutral",
              ownerName:
                d.owner?.name || d.uploadedBy?.name || d.ownerType || "Neutral",
              ownerType: d.ownerType,
              visibilityType: d.visibilityType || "all",
              pageCount: rawPages ? Number(rawPages) : null,
              tabNum,
              bundleCode: "",
              tabRef,
              documentNo: refNumber,
              ref: refNumber,
            };
          })
          .filter((d) => d.s3url && detectFileType(d.s3url) === "pdf");
        setCoreCompareDocs(pdfDocs);

        // Asynchronously probe page counts for PDFs that don't have it
        const needsProbe = pdfDocs.filter((d) => d.pageCount === null);
        if (needsProbe.length > 0) {
          const CONCURRENCY = 2;
          const queue = [...needsProbe];
          const runProbing = async () => {
            while (queue.length > 0) {
              const batch = queue.splice(0, CONCURRENCY);
              const results = await Promise.all(
                batch.map(async (d) => {
                  // Range-read only the PDF trailer (cached per session)
                  // instead of downloading every file in full.
                  const pageCount = await getPdfPageCount(d.s3url, d);
                  return { id: d.id, pageCount: pageCount || null };
                }),
              );
              setCoreCompareDocs((prev) =>
                prev.map((doc) => {
                  const match = results.find((r) => r.id === doc.id);
                  return match && match.pageCount !== null
                    ? { ...doc, pageCount: match.pageCount }
                    : doc;
                }),
              );
            }
          };
          runProbing();
        }
      }
    } catch (err) {
      enqueueSnackbar("Failed to fetch documents for comparison", {
        variant: "error",
      });
    } finally {
      setLoadingCompareDocs(false);
    }
  }, [id, enqueueSnackbar]);

  // Extract Trial Bundle PDFs
  const trialPdfDocs = useMemo(() => {
    const list = [];
    (trialBundles || []).forEach((bundle) => {
      const bCode = bundle.bundleCode || bundle.code || bundle.id || "";
      (bundle.bundles || []).forEach((sf) => {
        (sf.assignments || []).forEach((a) => {
          const s3url = a.bundleDocumentUrl || a.fileUrl || a.url;
          if (s3url && detectFileType(s3url) === "pdf") {
            const tabNum = a.tabNum ?? a.tab_num ?? a.tabNumber ?? "";
            const rawTabRef =
              a.tabRef ||
              a.tab_ref ||
              a.tabRefNumber ||
              a.refNumber ||
              a.ref ||
              a.reference ||
              a.display_tab ||
              a.displayTab ||
              a.tab_code ||
              a.tabCode ||
              "";
            const tabRef =
              rawTabRef ||
              (bCode && tabNum !== "" ? `${bCode}/${tabNum}` : "") ||
              (sf.code &&
              sf.code !== "__main__" &&
              sf.code !== "__flat__" &&
              tabNum !== ""
                ? `${sf.code}/${tabNum}`
                : "") ||
              (tabNum !== "" ? `${tabNum}` : "");
            list.push({
              id: `trial-${bundle.id}-${a.id || a.sourceDocId || Math.random()}-${a.tabNum}`,
              name: a.userLabel || a.name || `Tab ${a.tabNum}`,
              s3url,
              bundleName: bundle.name,
              bundleCode: bCode,
              folderName: sf.name || sf.code,
              tabNum: a.tabNum,
              tabRef: tabRef,
              visibilityType:
                a.visibilityType ||
                sf.visibilityType ||
                bundle.visibilityType ||
                "me",
              pageCount: parsePageCount(a.pages),
            });
          }
        });
      });
    });
    return list;
  }, [trialBundles]);

  // Filter comparison documents based on search query
  const filteredCoreDocs = useMemo(() => {
    const query = compareSearchQuery.trim().toLowerCase();
    if (!query) return coreCompareDocs;
    return coreCompareDocs.filter((d) => {
      const docName = (d.name || "").toLowerCase();
      const tabRef = (d.tabRef || "").toLowerCase();
      const docNo = (
        d.documentNo != null ? String(d.documentNo) : ""
      ).toLowerCase();
      const ref = (d.ref != null ? String(d.ref) : "").toLowerCase();

      return (
        docName.includes(query) ||
        (tabRef && tabRef.includes(query)) ||
        (docNo && docNo.includes(query)) ||
        (ref && ref.includes(query))
      );
    });
  }, [coreCompareDocs, compareSearchQuery]);

  const filteredTrialDocs = useMemo(() => {
    const query = compareSearchQuery.trim().toLowerCase();
    if (!query) return trialPdfDocs;
    return trialPdfDocs.filter((d) => {
      const docName = (d.name || "").toLowerCase();
      const bundleName = (d.bundleName || "").toLowerCase();
      const folderName = (d.folderName || "").toLowerCase();
      const tabRef = (d.tabRef || "").toLowerCase();
      const tabNumStr = d.tabNum != null ? String(d.tabNum).toLowerCase() : "";
      const bundleCode = (d.bundleCode || "").toLowerCase();
      const fullRef =
        bundleCode && tabNumStr ? `${bundleCode}/${tabNumStr}` : "";

      return (
        docName.includes(query) ||
        bundleName.includes(query) ||
        folderName.includes(query) ||
        tabRef.includes(query) ||
        (tabNumStr && tabNumStr.includes(query)) ||
        (fullRef && fullRef.includes(query)) ||
        (bundleCode && bundleCode.includes(query))
      );
    });
  }, [trialPdfDocs, compareSearchQuery]);

  // Hyperlink modal document lists (uses hyperlinkSearchQuery, otherwise same as Split View)
  const filteredHlCoreDocs = useMemo(() => {
    const query = hyperlinkSearchQuery.trim().toLowerCase();
    if (!query) return coreCompareDocs;
    return coreCompareDocs.filter((d) => {
      const docName = (d.name || "").toLowerCase();
      const tabRef = (d.tabRef || "").toLowerCase();
      const docNo = (
        d.documentNo != null ? String(d.documentNo) : ""
      ).toLowerCase();
      const ref = (d.ref != null ? String(d.ref) : "").toLowerCase();
      return (
        docName.includes(query) ||
        (tabRef && tabRef.includes(query)) ||
        (docNo && docNo.includes(query)) ||
        (ref && ref.includes(query))
      );
    });
  }, [coreCompareDocs, hyperlinkSearchQuery]);

  const filteredHlTrialDocs = useMemo(() => {
    const query = hyperlinkSearchQuery.trim().toLowerCase();
    if (!query) return trialPdfDocs;
    return trialPdfDocs.filter((d) => {
      const docName = (d.name || "").toLowerCase();
      const bundleName = (d.bundleName || "").toLowerCase();
      const folderName = (d.folderName || "").toLowerCase();
      const tabRef = (d.tabRef || "").toLowerCase();
      const tabNumStr = d.tabNum != null ? String(d.tabNum).toLowerCase() : "";
      const bundleCode = (d.bundleCode || "").toLowerCase();
      const fullRef =
        bundleCode && tabNumStr ? `${bundleCode}/${tabNumStr}` : "";
      return (
        docName.includes(query) ||
        bundleName.includes(query) ||
        folderName.includes(query) ||
        tabRef.includes(query) ||
        (tabNumStr && tabNumStr.includes(query)) ||
        (fullRef && fullRef.includes(query)) ||
        (bundleCode && bundleCode.includes(query))
      );
    });
  }, [trialPdfDocs, hyperlinkSearchQuery]);

  // Reset on doc change
  // Reset on doc change
  useEffect(() => {
    const savedPosition = documentPagePositions.get(
      getDocumentPagePositionKey(doc),
    );
    const urlPage = getUrlPageParam();
    const targetPage = doc?.targetPage || doc?.initialPage || null;
    const savedPage = targetPage || urlPage || savedPosition?.page || 1;

    setCurrentPage(savedPage);
    setPageInputValue(savedPage);
    setNumPages(null);
    setZoom(savedPosition?.zoom || 90);
    setNoteMode(false);
    setScrollTargetPage(
      targetPage || urlPage || savedPosition ? savedPage : null,
    );
    setSinglePageView(
      targetPage ? true : savedPosition?.singlePageView || false,
    );
    setAnnotations(loadAnnotations(doc.id));
    setNotes(loadNotes(doc.id));
    setHyperlinks(loadHyperlinks(doc.id));
    setHyperlinkMode(false);
    setRedactMode(false);
    setHyperlinkPending(null);
    setShowHyperlinkDocModal(false);
    setSelectedHlDoc(null);
    setHlTargetPage("");
    setHlPageError("");
    setMainDocOverride(null);
    setCompareDoc(null);
    setCompareOpen(false);
    setCompareSearchQuery("");
    setCompareSinglePageView(false);
    setCompareAnnotations([]);
    setCompareNotes([]);
    setPageSize({ width: 595, height: 842 });
    setComparePageSize({ width: 595, height: 842 });
    setComparePageInputValue(1);
    // Allow auto-fit to recalculate when a new document opens
    isUserZoomedRef.current = false;

    // Once consumed, drop ?page= from the address bar so later in-app
    // navigation (switching bundles/tabs) doesn't get stuck on it.
    if (urlPage) clearUrlPageParam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id, doc.s3url]);

  useEffect(() => {
    if (doc?.targetPage) {
      const p = Number(doc.targetPage);
      if (Number.isFinite(p) && p > 0) {
        setSinglePageView(true);
        setCurrentPage(p);
        setPageInputValue(p);
        setScrollTargetPage(p);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.targetPage, doc?._hlNavTs]);

  const fitMainPage = useCallback(() => {
    const container = canvasRef.current;
    if (!container || !pageSize) return;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth > 0 && containerHeight > 0) {
      const style = window.getComputedStyle(container);
      const padTop = parseFloat(style.paddingTop) || 0;
      const padBottom = parseFloat(style.paddingBottom) || 0;
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      const availableWidth = containerWidth - padLeft - padRight;
      const availableHeight = containerHeight - padTop - padBottom;
      const scaleX = availableWidth / pageSize.width;
      const scaleY = availableHeight / pageSize.height;
      const fitZoom = Math.min(scaleX, scaleY) * 100;
      setZoom(Math.min(220, Math.max(30, Math.round(fitZoom))));
    }
  }, [pageSize]);

  const fitComparePage = useCallback(() => {
    const container = compareCanvasRef.current;
    if (!container || !comparePageSize) return;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth > 0 && containerHeight > 0) {
      const style = window.getComputedStyle(container);
      const padTop = parseFloat(style.paddingTop) || 0;
      const padBottom = parseFloat(style.paddingBottom) || 0;
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      const availableWidth = containerWidth - padLeft - padRight;
      const availableHeight = containerHeight - padTop - padBottom;
      const scaleX = availableWidth / comparePageSize.width;
      const scaleY = availableHeight / comparePageSize.height;
      const fitZoom = Math.min(scaleX, scaleY) * 100;
      setCompareZoom(Math.min(220, Math.max(30, Math.round(fitZoom))));
    }
  }, [comparePageSize]);

  const handleFitWidth = () => {
    const container = canvasRef.current;
    if (!container || !pageSize) return;
    const containerWidth = container.clientWidth;
    if (containerWidth > 0) {
      const padding = 72;
      const fitZoom = ((containerWidth - padding) / pageSize.width) * 100;
      setZoom(Math.min(220, Math.max(30, Math.round(fitZoom))));
    }
  };

  const handleFitPage = () => {
    // Reset user-zoom flag so auto-fit can work again after explicit "Fit page"
    isUserZoomedRef.current = false;
    fitMainPage();
    if (compareOpen) {
      fitComparePage();
    }
  };

  useEffect(() => {
    if (effectiveMainDoc?.id) {
      saveAnnotations(effectiveMainDoc.id, annotations);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMainDoc?.id, annotations]);

  useEffect(() => {
    if (effectiveMainDoc?.id) {
      saveNotes(effectiveMainDoc.id, notes);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMainDoc?.id, notes]);

  useEffect(() => {
    if (effectiveMainDoc?.id) {
      saveHyperlinks(effectiveMainDoc.id, hyperlinks);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMainDoc?.id, hyperlinks]);

  useEffect(() => {
    if (!compareOpen && mainDocOverride) {
      setMainDocOverride(null);
      setAnnotations(loadAnnotations(doc.id));
      setNotes(loadNotes(doc.id));
    }
  }, [compareOpen, doc.id, mainDocOverride]);

  useEffect(() => {
    if (compareDoc?.id) saveAnnotations(compareDoc.id, compareAnnotations); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareAnnotations]);
  useEffect(() => {
    if (compareDoc?.id) saveNotes(compareDoc.id, compareNotes); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareNotes]);

  // Automatically fit pages whenever the scroll containers change dimensions
  useEffect(() => {
    const mainEl = canvasRef.current;
    const compareEl = compareCanvasRef.current;
    let rafId = null;
    let timeoutId = null;

    const observer = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      // debounce 120ms so continuous panel-resize/drag doesn't spam fitPage
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          if (suppressAutoFitRef.current) return;
          // Do not override a zoom level the user has explicitly set.
          // This prevents scrollbar toggling (caused by zoom changes) from
          // triggering a resize event that resets zoom back to fit-page.
          if (isUserZoomedRef.current) return;
          if (mainEl) fitMainPage();
          if (compareEl) fitComparePage();
        });
      }, 120);
    });

    if (mainEl) observer.observe(mainEl);
    if (compareEl) observer.observe(compareEl);

    fitMainPage();
    fitComparePage();

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fitMainPage, fitComparePage, compareOpen, isFullscreen]);

  // Automatically close side panel when compare view is opened, and reopen it when closed if it was open
  useEffect(() => {
    if (compareOpen) {
      if (rightOpen) {
        setWasRightPanelOpenBeforeCompare(true);
        setRightOpen(false);
      } else {
        setWasRightPanelOpenBeforeCompare(false);
      }
    } else {
      if (wasRightPanelOpenBeforeCompare) {
        setRightOpen(true);
        setWasRightPanelOpenBeforeCompare(false);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareOpen]);

  // Notify parent component (CoreBundle) when compare view is toggled
  useEffect(() => {
    if (typeof onCompareToggle === "function") {
      onCompareToggle(compareOpen);
    }
    return () => {
      if (typeof onCompareToggle === "function") {
        onCompareToggle(false);
      }
    };
  }, [compareOpen, onCompareToggle]);

  // Close dropdowns on outside click
  useEffect(() => {
    const h = (e) => {
      if (dlRef.current && !dlRef.current.contains(e.target)) setDlOpen(false);
      if (zoomRef.current && !zoomRef.current.contains(e.target))
        setZoomMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Fullscreen & escape key
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (hyperlinkMode) {
          setHyperlinkModeState(false);
          return;
        }
        if (noteMode) {
          setNoteModeState(false);
          return;
        }
        if (document.fullscreenElement)
          document.exitFullscreen().catch(() => {});
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("keydown", onKeyDown);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteMode, hyperlinkMode]);

  useEffect(() => {
    if (isFullscreen) {
      setZoom(100);
    } else {
      setZoom(90);
    }
  }, [isFullscreen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && activeDoc) onBack();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDoc]);

  const toggleFullscreen = () => {
    const shareUrl = buildShareableDocUrl(isPdf ? currentPage : null);
    // localStorage.setItem("open_doc", JSON.stringify())
    window.open(shareUrl || window.location.href, "_blank");
  };

  const setNoteModeState = useCallback((value) => {
    suppressAutoFitRef.current = true;
    setNoteMode((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      return next;
    });
    window.requestAnimationFrame(() => {
      suppressAutoFitRef.current = false;
    });
  }, []);

  // Annotation / note CRUD
  const addAnnot = useCallback((a) => setAnnotations((p) => [a, ...p]), []);
  const deleteAnnot = useCallback(
    (id) => setAnnotations((p) => p.filter((x) => x.id !== id)),
    [],
  );
  const addNote = useCallback(
    (n) => {
      setNotes((p) => [n, ...p]);
      setNoteModeState(false);
    },
    [setNoteModeState],
  );
  const deleteNote = useCallback(
    (id) => setNotes((p) => p.filter((x) => x.id !== id)),
    [],
  );

  // Hyperlink CRUD
  const setHyperlinkModeState = useCallback((value) => {
    // Turn off noteMode when activating hyperlink mode
    setHyperlinkMode((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (next) setNoteMode(false);
      return next;
    });
  }, []);

  const handleAddHyperlink = useCallback(
    (position) => {
      // position = { page, x, y, cssScale }
      setHyperlinkPending(position);
      setHyperlinkSearchQuery("");
      setSelectedHlDoc(null);
      setHlTargetPage("1");
      setHlPageError("");
      if (coreCompareDocs.length === 0) fetchAllCoreDocs();
      setShowHyperlinkDocModal(true);
    },
    [coreCompareDocs.length, fetchAllCoreDocs],
  );

  const confirmHyperlink = useCallback(
    (targetDoc, pageNumber) => {
      if (!hyperlinkPending || !targetDoc) return;
      const pageNum = Number(pageNumber || 1);
      const formattedTarget = getFormattedHyperlinkTarget(targetDoc, pageNum);

      const hl = {
        id: uid(),
        page: hyperlinkPending.page,
        x: hyperlinkPending.x,
        y: hyperlinkPending.y,
        cssScale: hyperlinkPending.cssScale,
        target: formattedTarget,
        targetRef: formattedTarget,
        targetPage: pageNum,
        targetDoc: {
          id: targetDoc.id,
          name: targetDoc.name,
          s3url: targetDoc.s3url,
          bundleName: targetDoc.bundleName,
          bundleCode: targetDoc.bundleCode,
          folderName: targetDoc.folderName,
          tabRef: targetDoc.tabRef,
          tabNum: targetDoc.tabNum,
          documentNo: targetDoc.documentNo,
          ref: targetDoc.ref,
          ownerType: targetDoc.ownerType,
          visibilityType: targetDoc.visibilityType,
          pageCount: targetDoc.pageCount,
          targetPage: pageNum,
        },
        time: new Date().toLocaleTimeString(),
      };
      setHyperlinks((prev) => [...prev, hl]);
      setHyperlinkPending(null);
      setShowHyperlinkDocModal(false);
      setSelectedHlDoc(null);
      setHlTargetPage("");
      setHlPageError("");
      setHyperlinkModeState(false);
    },
    [hyperlinkPending, setHyperlinkModeState],
  );

  const deleteHyperlink = useCallback(
    (id) => setHyperlinks((p) => p.filter((x) => x.id !== id)),
    [],
  );

  const updateHyperlinkNote = useCallback((id, note) => {
    setHyperlinks((prev) =>
      prev.map((hl) => (hl.id === id ? { ...hl, note } : hl)),
    );
  }, []);

  const handleOpenLinkedDocHyperlink = useCallback(
    (targetDoc, targetPage) => {
      if (!targetDoc) return;
      const rawPage = targetPage || targetDoc?.targetPage;
      const pageNum = rawPage ? Number(rawPage) : null;
      const docWithPage = pageNum
        ? {
            ...targetDoc,
            targetPage: pageNum,
            initialPage: pageNum,
            _openInPageView: true,
            _hlNavTs: Date.now(),
          }
        : targetDoc;

      if (pageNum) {
        const key = getDocumentPagePositionKey(docWithPage);
        const existingPos = documentPagePositions.get(key) || {};
        documentPagePositions.set(key, {
          ...existingPos,
          page: Number(pageNum),
          singlePageView: true,
        });
      }

      const currentDocKey = getDocumentPagePositionKey(effectiveMainDoc);
      const targetDocKey = getDocumentPagePositionKey(docWithPage);
      if (currentDocKey === targetDocKey && pageNum) {
        setSinglePageView(true);
        setCurrentPage(Number(pageNum));
        setPageInputValue(Number(pageNum));
        setScrollTargetPage(Number(pageNum));
        return;
      }

      if (typeof onOpenLinkedDoc === "function") {
        onOpenLinkedDoc(docWithPage);
      }
    },
    [effectiveMainDoc, onOpenLinkedDoc],
  );

  const addCompareAnnot = useCallback(
    (a) => setCompareAnnotations((p) => [a, ...p]),
    [],
  );
  const deleteCompareAnnot = useCallback(
    (id) => setCompareAnnotations((p) => p.filter((x) => x.id !== id)),
    [],
  );
  const addCompareNote = useCallback(
    (n) => {
      setCompareNotes((p) => [n, ...p]);
      setNoteModeState(false);
    },
    [setNoteModeState],
  );
  const deleteCompareNote = useCallback(
    (id) => setCompareNotes((p) => p.filter((x) => x.id !== id)),
    [],
  );

  const isCompareActive =
    compareOpen && compareDoc && activeCompareSide === "compare";
  const activeDocObj = isCompareActive ? compareDoc : effectiveMainDoc;
  const activeAnnotations = isCompareActive ? compareAnnotations : annotations;
  const activeNotes = isCompareActive ? compareNotes : notes;
  const activeDeleteAnnot = isCompareActive ? deleteCompareAnnot : deleteAnnot;
  const activeDeleteNote = isCompareActive ? deleteCompareNote : deleteNote;
  const activeNumPages = isCompareActive ? compareNumPages : numPages;
  const activeFt = detectFileType(activeDocObj?.s3url);

  // Zoom with viewport anchor
  const rememberViewportCenter = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    zoomAnchorRef.current = {
      x:
        el.scrollWidth > 0
          ? (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth
          : 0.5,
      y:
        el.scrollHeight > 0
          ? (el.scrollTop + el.clientHeight / 2) / el.scrollHeight
          : 0.5,
    };
  }, []);

  useLayoutEffect(() => {
    const el = canvasRef.current;
    const anchor = zoomAnchorRef.current;
    if (!el || !anchor) return;
    el.scrollTo({
      left: anchor.x * el.scrollWidth - el.clientWidth / 2,
      top: anchor.y * el.scrollHeight - el.clientHeight / 2,
      behavior: "auto",
    });
  }, [zoom]);

  const animateZoom = (target) => {
    rememberViewportCenter();
    setZoom(target);
  };

  const handleZoomIn = () => {
    isUserZoomedRef.current = true;
    animateZoom(Math.min(220, Math.round(zoom / 10) * 10 + 10));
  };
  const handleZoomOut = () => {
    isUserZoomedRef.current = true;
    animateZoom(Math.max(30, Math.round(zoom / 10) * 10 - 10));
  };
  const handleSetZoom = (v) => {
    isUserZoomedRef.current = true;
    animateZoom(v);
  };

  // Page navigation — clamps value and signals PdfViewer to scroll
  const goToPage = useCallback(
    (page) => {
      if (!isPdf) return;
      const maxPage = numPages || 1;
      const nextPage = Math.min(maxPage, Math.max(1, Number(page) || 1));

      isProgrammaticScrollRef.current = true;
      setCurrentPage(nextPage);
      setPageInputValue(nextPage);
      setSinglePageView(true);
      setScrollTargetPage(nextPage);

      // Reset programmatic scroll flag after scroll animation completes
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 800);
    },
    [isPdf, numPages],
  );

  const handlePageInputChange = useCallback((event) => {
    // A text field avoids native number-input quirks where multi-digit page
    // numbers can lose their selection while the value is being edited.
    setPageInputValue(event.target.value.replace(/\D/g, ""));
  }, []);

  const commitPageInput = useCallback(() => {
    const page = Number(pageInputValue);
    if (page >= 1 && page <= (numPages || 1)) {
      goToPage(page);
    } else {
      setPageInputValue(currentPage);
    }
  }, [currentPage, goToPage, numPages, pageInputValue]);

  const togglePageView = useCallback(() => {
    // Keep the page the user is reading in view while changing layouts.
    setSinglePageView((isSinglePage) => !isSinglePage);
    setScrollTargetPage(currentPage);
  }, [currentPage]);

  const toggleComparePageView = useCallback(() => {
    setCompareSinglePageView((v) => !v);
    setCompareScrollTargetPage(comparePage);
  }, [comparePage]);

  // Keep split-pane page navigation identical to the original viewer: entering
  // a page number (or using arrows) focuses that page, while the view-mode
  // button lets the user return to continuous scrolling.
  const goToComparePage = useCallback(
    (page) => {
      const maxPage = compareNumPages || 1;
      const nextPage = Math.min(maxPage, Math.max(1, Number(page) || 1));

      isCompareProgrammaticScrollRef.current = true;
      setComparePage(nextPage);
      setComparePageInputValue(nextPage);
      setCompareSinglePageView(true);
      setCompareScrollTargetPage(nextPage);

      setTimeout(() => {
        isCompareProgrammaticScrollRef.current = false;
      }, 800);
    },
    [compareNumPages],
  );

  const handleComparePageInputChange = useCallback((event) => {
    setComparePageInputValue(event.target.value.replace(/\D/g, ""));
  }, []);

  const commitComparePageInput = useCallback(() => {
    const page = Number(comparePageInputValue);
    if (page >= 1 && page <= (compareNumPages || 1)) {
      goToComparePage(page);
    } else {
      setComparePageInputValue(comparePage);
    }
  }, [compareNumPages, comparePage, comparePageInputValue, goToComparePage]);

  const getTabKey = (tab) =>
    [
      tab?.id,
      tab?.sourceDocId,
      tab?.bundlecode,
      tab?.bundleId,
      tab?.s3url,
      tab?.name,
    ]
      .filter((value) => value !== undefined && value !== null && value !== "")
      .join("::");

  const changeVisibility = async (value) => {
    try {
      setLoader({ state: true, message: "Updating visibility…" });
      let res;
      if (doc?.bundle) {
        res = await BundleService.update_bundle(doc.id, {
          visibilityType: value,
          sourceDocId: doc.sourceDocId,
          prefix: doc.bundlecode,
        });
        if (typeof fetchAllBundles === "function") fetchAllBundles();
      } else {
        res = await CaseService.changeDocType(
          { visibilityType: value },
          id,
          doc.id,
        );
        if (typeof setTrigger === "function") setTrigger((t) => !t);
      }
      setVisibility(value);
      if (typeof onVisibilityChange === "function") {
        onVisibilityChange(doc, value);
      }
      if (res) enqueueSnackbar("Visibility updated", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  // ── Interactive viewing: shortcuts, gesture zoom, swipe paging ─────────
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [zoomToast, setZoomToast] = useState(null);
  const zoomToastTimerRef = useRef(null);
  const isSplitActive = !!(compareOpen && compareDoc);

  const flashZoom = useCallback((value) => {
    setZoomToast(Math.round(value));
    if (zoomToastTimerRef.current) clearTimeout(zoomToastTimerRef.current);
    zoomToastTimerRef.current = setTimeout(() => setZoomToast(null), 900);
  }, []);

  useEffect(
    () => () => {
      if (zoomToastTimerRef.current) clearTimeout(zoomToastTimerRef.current);
    },
    [],
  );

  // Handlers are recreated every render; the listeners below read the latest
  // versions through this ref so they can be attached once.
  const viewerActionsRef = useRef({});
  viewerActionsRef.current = {
    isPdf,
    isSplitActive,
    currentPage,
    numPages,
    zoom,
    goToPage,
    togglePageView,
    handleFitPage,
    handleFitWidth,
    singlePageView,
    noteMode,
    hyperlinkMode,
    modalOpen: showSelectCompareModal || showHyperlinkDocModal,
    zoomBy: (delta) =>
      viewerActionsRef.current.zoomTo(Math.round(zoom / 10) * 10 + delta),
    zoomTo: (value) => {
      isUserZoomedRef.current = true;
      rememberViewportCenter();
      const next = Math.min(220, Math.max(30, Math.round(value)));
      setZoom(next);
      flashZoom(next);
    },
    toggleNoteMode: () => setNoteModeState((o) => !o),
    togglePanel: () => setRightOpen((o) => !o),
    toggleShortcuts: () => setShortcutsOpen((o) => !o),
  };

  // Keyboard shortcuts (ignored while typing or when a modal is open)
  useEffect(() => {
    const onKeyDown = (e) => {
      const a = viewerActionsRef.current;
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        t?.isContentEditable ||
        a.modalOpen
      )
        return;

      const key = e.key;
      if (key === "?") {
        e.preventDefault();
        a.toggleShortcuts();
        return;
      }
      if (a.isSplitActive) return;

      if (a.isPdf && (key === "ArrowRight" || key === "ArrowLeft")) {
        e.preventDefault();
        a.goToPage(a.currentPage + (key === "ArrowRight" ? 1 : -1));
      } else if (key === "+" || key === "=") {
        e.preventDefault();
        a.zoomBy(10);
      } else if (key === "-" || key === "_") {
        e.preventDefault();
        a.zoomBy(-10);
      } else if (key === "0") {
        e.preventDefault();
        a.handleFitPage();
      } else if (key === "w" || key === "W") {
        e.preventDefault();
        a.handleFitWidth();
      } else if (a.isPdf && (key === "v" || key === "V")) {
        e.preventDefault();
        a.togglePageView();
      } else if (key === "n" || key === "N") {
        e.preventDefault();
        a.toggleNoteMode();
      } else if (key === "p" || key === "P") {
        e.preventDefault();
        a.togglePanel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Redact mode is exclusive with note/hyperlink placement and split view.
  useEffect(() => {
    if (noteMode || hyperlinkMode || compareOpen) setRedactMode(false);
  }, [noteMode, hyperlinkMode, compareOpen]);

  const toggleRedactMode = () =>
    setRedactMode((prev) => {
      if (!prev) {
        setNoteMode(false);
        setHyperlinkMode(false);
      }
      return !prev;
    });

  // Esc leaves redact mode instead of closing the viewer.
  useEffect(() => {
    if (!redactMode) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      setRedactMode(false);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [redactMode]);

  // Esc closes the shortcuts sheet first, before the viewer's own Esc handling.
  useEffect(() => {
    if (!shortcutsOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      setShortcutsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [shortcutsOpen]);

  // Ctrl/⌘ + wheel (and trackpad pinch) zoom, touch pinch zoom, swipe paging.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;

    let wheelAccum = 0;
    let wheelRaf = null;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      wheelAccum += e.deltaY;
      if (wheelRaf) return;
      wheelRaf = requestAnimationFrame(() => {
        const a = viewerActionsRef.current;
        const factor = Math.exp(-wheelAccum * 0.0025);
        wheelAccum = 0;
        wheelRaf = null;
        a.zoomTo(a.zoom * factor);
      });
    };

    let pinch = null;
    let swipe = null;
    const distance = (t1, t2) =>
      Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    const previewTarget = () => el.firstElementChild;

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        swipe = null;
        const [t1, t2] = e.touches;
        const rect = el.getBoundingClientRect();
        pinch = {
          startDist: distance(t1, t2),
          ratio: 1,
          originX: (t1.clientX + t2.clientX) / 2 - rect.left + el.scrollLeft,
          originY: (t1.clientY + t2.clientY) / 2 - rect.top + el.scrollTop,
        };
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        swipe = { x: t.clientX, y: t.clientY, time: Date.now() };
      }
    };

    const onTouchMove = (e) => {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const [t1, t2] = e.touches;
      const a = viewerActionsRef.current;
      const minRatio = 30 / a.zoom;
      const maxRatio = 220 / a.zoom;
      pinch.ratio = Math.min(
        maxRatio,
        Math.max(minRatio, distance(t1, t2) / pinch.startDist),
      );
      const target = previewTarget();
      if (target) {
        target.style.transformOrigin = `${pinch.originX}px ${pinch.originY}px`;
        target.style.transform = `scale(${pinch.ratio})`;
      }
      flashZoom(a.zoom * pinch.ratio);
    };

    const onTouchEnd = (e) => {
      if (pinch && e.touches.length < 2) {
        const target = previewTarget();
        if (target) {
          target.style.transform = "";
          target.style.transformOrigin = "";
        }
        const a = viewerActionsRef.current;
        if (Math.abs(pinch.ratio - 1) > 0.03) a.zoomTo(a.zoom * pinch.ratio);
        pinch = null;
        return;
      }
      if (!swipe || e.changedTouches.length !== 1) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - swipe.x;
      const dy = t.clientY - swipe.y;
      const dt = Date.now() - swipe.time;
      swipe = null;
      const a = viewerActionsRef.current;
      const canPanHorizontally = el.scrollWidth > el.clientWidth + 2;
      if (
        a.isPdf &&
        a.singlePageView &&
        !a.noteMode &&
        !a.hyperlinkMode &&
        !canPanHorizontally &&
        dt < 600 &&
        Math.abs(dx) > 60 &&
        Math.abs(dy) < 50
      ) {
        a.goToPage(a.currentPage + (dx < 0 ? 1 : -1));
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      if (wheelRaf) cancelAnimationFrame(wheelRaf);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
    // The canvas element is swapped out when split view toggles.
  }, [isSplitActive, flashZoom]);

  const readProgress =
    isPdf && numPages ? Math.min(100, (currentPage / numPages) * 100) : 0;

  return (
    <AppWrapper ref={appRef} $fullscreen={isFullscreen}>
      {/* ── Tab bar ── */}
      <TabBar>
        <BackButton onClick={onBack}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <BackLabel>Back</BackLabel>
        </BackButton>
        <TabDivider />
        <TabScroll>
          {(() => {
            const isCompareMode = compareOpen && compareDoc;
            const isCompareDocInOpenTabs =
              isCompareMode &&
              openTabs.some((t) => getTabKey(t) === getTabKey(compareDoc));

            const renderCompareTab = (cDoc) => (
              <DocTab
                key={`compare-${getTabKey(cDoc)}`}
                $active={isCompareMode && activeCompareSide === "compare"}
                onClick={() => {
                  if (isCompareMode) {
                    setActiveCompareSide("compare");
                  }
                }}
              >
                <TabFilePill $ft={detectFileType(cDoc.s3url)}>
                  {detectFileType(cDoc.s3url) === "pdf" ? "PDF" : "DOC"}
                </TabFilePill>
                <TabName>
                  {cDoc.name.length > 24
                    ? cDoc.name.slice(0, 24) + "…"
                    : cDoc.name}
                </TabName>
                <TabCloseBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    setCompareDoc(null);
                    setCompareOpen(false);
                  }}
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </TabCloseBtn>
              </DocTab>
            );

            if (!openTabs || openTabs.length === 0) {
              return isCompareMode ? renderCompareTab(compareDoc) : null;
            }

            return openTabs.map((t) => {
              const isMainDoc = getTabKey(t) === getTabKey(doc);
              const isCompDoc =
                isCompareMode && getTabKey(t) === getTabKey(compareDoc);

              if (isCompDoc && !isMainDoc) {
                return renderCompareTab(t);
              }

              const isActive = isCompareMode
                ? isMainDoc && activeCompareSide === "original"
                : isMainDoc;

              return (
                <React.Fragment key={`tab-group-${getTabKey(t)}`}>
                  <DocTab
                    key={getTabKey(t)}
                    $active={isActive}
                    onClick={() => {
                      if (isCompareMode && isMainDoc) {
                        setActiveCompareSide("original");
                      } else {
                        onTabClick(t);
                      }
                    }}
                  >
                    <TabFilePill $ft={detectFileType(t.s3url)}>
                      {detectFileType(t.s3url) === "pdf" ? "PDF" : "DOC"}
                    </TabFilePill>
                    <TabName>
                      {(() => {
                        const displayDoc =
                          isMainDoc && isCompareMode && mainDocOverride
                            ? mainDocOverride
                            : t;
                        return displayDoc.name.length > 24
                          ? displayDoc.name.slice(0, 24) + "…"
                          : displayDoc.name;
                      })()}
                    </TabName>
                    <TabCloseBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        onTabClose(t);
                      }}
                    >
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </TabCloseBtn>
                  </DocTab>
                  {isMainDoc &&
                    isCompareMode &&
                    !isCompareDocInOpenTabs &&
                    renderCompareTab(compareDoc)}
                </React.Fragment>
              );
            });
          })()}
        </TabScroll>
      </TabBar>

      {/* ── Body ── */}
      <ViewerBody>
        <MainArea>
          {/* ── Toolbar ── */}
          <Toolbar>
            <ToolLeft>
              {isPdf && !(compareOpen && compareDoc) && (
                <>
                  <PageNav>
                    <PageBtn
                      $secondary
                      onClick={onPrevDoc}
                      disabled={!hasPrevDoc}
                      title="Previous document"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="11 18 5 12 11 6" />
                        <polyline points="18 18 12 12 18 6" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      $secondary
                      onClick={() => goToPage(1)}
                      disabled={currentPage <= 1}
                      title="First page"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <line x1="6" y1="6" x2="6" y2="18" />
                        <polyline points="17 18 11 12 17 6" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      title="Previous page (←)"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </PageBtn>
                    <PageInput
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={pageInputValue}
                      onChange={handlePageInputChange}
                      onFocus={(e) => e.currentTarget.select()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          commitPageInput();
                          const target = e.currentTarget;
                          setTimeout(() => {
                            if (target) {
                              target.focus();
                              target.select();
                            }
                          }, 0);
                        }
                      }}
                      onBlur={commitPageInput}
                    />
                    {numPages && <PageTotal>/ {numPages}</PageTotal>}
                    <PageBtn
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={!numPages || currentPage >= numPages}
                      title="Next page (→)"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      $secondary
                      onClick={() => numPages && goToPage(numPages)}
                      disabled={!numPages || currentPage >= numPages}
                      title="Last page"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="7 18 13 12 7 6" />
                        <line x1="18" y1="6" x2="18" y2="18" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      $secondary
                      onClick={onNextDoc}
                      disabled={!hasNextDoc}
                      title="Next document"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="6 18 12 12 6 6" />
                        <polyline points="13 18 19 12 13 6" />
                      </svg>
                    </PageBtn>
                  </PageNav>
                  <ViewModeBtn
                    onClick={togglePageView}
                    title={`${singlePageView ? "Scroll View" : "Page View"} (V)`}
                  >
                    {singlePageView ? (
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="6" y="3" width="12" height="7" rx="1.5" />
                        <rect x="6" y="14" width="12" height="7" rx="1.5" />
                      </svg>
                    ) : (
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="5" y="3" width="14" height="18" rx="2" />
                        <line x1="9" y1="7" x2="15" y2="7" />
                        <line x1="9" y1="11" x2="15" y2="11" />
                        <line x1="9" y1="15" x2="13" y2="15" />
                      </svg>
                    )}
                  </ViewModeBtn>
                  <ToolDivider />
                </>
              )}

              {!(compareOpen && compareDoc) && (
                <ZoomGroup>
                  <ZoomBtn
                    onClick={handleZoomOut}
                    disabled={zoom <= 30}
                    title="Zoom out (−)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </ZoomBtn>
                  <ZoomWrap ref={zoomRef}>
                    <ZoomLabel onClick={() => setZoomMenuOpen((o) => !o)}>
                      {Math.round(zoom)}%
                    </ZoomLabel>
                    {zoomMenuOpen && (
                      <ZoomDropdown>
                        <ZoomItem
                          onClick={() => {
                            handleFitWidth();
                            setZoomMenuOpen(false);
                          }}
                        >
                          Fit width
                        </ZoomItem>
                        <ZoomItem
                          onClick={() => {
                            handleFitPage();
                            setZoomMenuOpen(false);
                          }}
                        >
                          Fit page
                        </ZoomItem>
                        {ZOOM_PRESETS.map((z) => (
                          <ZoomItem
                            key={z}
                            $active={Math.round(zoom) === z}
                            onClick={() => {
                              handleSetZoom(z);
                              setZoomMenuOpen(false);
                            }}
                          >
                            {z}%
                          </ZoomItem>
                        ))}
                      </ZoomDropdown>
                    )}
                  </ZoomWrap>
                  <ZoomBtn
                    onClick={handleZoomIn}
                    disabled={zoom >= 220}
                    title="Zoom in (+)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <line x1="11" y1="8" x2="11" y2="14" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </ZoomBtn>
                </ZoomGroup>
              )}
            </ToolLeft>

            <DocTitleArea>
              <DocTitle title={effectiveMainDoc.name}>
                {effectiveMainDoc.name.length > 48
                  ? effectiveMainDoc.name.slice(0, 48) + "…"
                  : effectiveMainDoc.name}
              </DocTitle>
              <DocSubtitle>
                {detectFileType(effectiveMainDoc.s3url).toUpperCase()}
                {numPages ? ` · ${numPages} pages` : ""}
              </DocSubtitle>
            </DocTitleArea>

            <ToolRight>
              {isPdf &&
                (activeSection === "trial" ||
                  activeSection === "private" ||
                  doc.bundle) && (
                  <>
                    <CompareBtn
                      $active={compareOpen}
                      onClick={() => {
                        if (compareOpen) {
                          setCompareDoc(null);
                          setMainDocOverride(null);
                          setCompareOpen(false);
                        } else {
                          setSelectCompareSide("compare");
                          setShowSelectCompareModal(true);
                          if (coreCompareDocs.length === 0) {
                            fetchAllCoreDocs();
                          }
                        }
                      }}
                      title={compareOpen ? "Close Split" : "Split View"}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="12" y1="3" x2="12" y2="21" />
                      </svg>
                    </CompareBtn>
                    <ToolDivider />
                  </>
                )}
              <NoteModeToggle
                $active={noteMode}
                onClick={() => setNoteModeState((o) => !o)}
                title="Add Note (N)"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </NoteModeToggle>
              {isPdf && (
                <HyperlinkModeToggle
                  $active={hyperlinkMode}
                  onClick={() => setHyperlinkModeState((o) => !o)}
                  title="Hyperlink"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </HyperlinkModeToggle>
              )}
              {isPdf && !compareOpen && (
                <HyperlinkModeToggle
                  $active={redactMode}
                  onClick={toggleRedactMode}
                  title="Redact"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="5" width="18" height="4" rx="1" fill="currentColor" />
                    <line x1="3" y1="13" x2="21" y2="13" />
                    <rect x="3" y="16" width="11" height="3" rx="1" fill="currentColor" />
                  </svg>
                </HyperlinkModeToggle>
              )}
              <ToolDivider />
              <DlWrap ref={dlRef}>
                <DownloadTrigger
                  onClick={() => setDlOpen((o) => !o)}
                  title="Download"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </DownloadTrigger>
                <DownloadMenu
                  open={dlOpen}
                  doc={activeDocObj}
                  annotations={activeAnnotations}
                  notes={activeNotes}
                  isPdf={activeFt === "pdf"}
                  onClose={() => setDlOpen(false)}
                />
              </DlWrap>
              <ToolDivider />
              <IconBtn onClick={toggleFullscreen} title="Open in new tab">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </IconBtn>
              <ShortcutsBtn
                $active={shortcutsOpen}
                onClick={() => setShortcutsOpen((o) => !o)}
                title="Keyboard shortcuts (?)"
                aria-label="Keyboard shortcuts"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <line x1="6" y1="10" x2="6" y2="10" />
                  <line x1="10" y1="10" x2="10" y2="10" />
                  <line x1="14" y1="10" x2="14" y2="10" />
                  <line x1="18" y1="10" x2="18" y2="10" />
                  <line x1="7" y1="14" x2="17" y2="14" />
                </svg>
              </ShortcutsBtn>
              <PanelToggle
                $active={rightOpen}
                onClick={() => setRightOpen((o) => !o)}
                title="Toggle panel (P)"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="15" y1="3" x2="15" y2="21" />
                </svg>
                {(activeAnnotations.length > 0 || activeNotes.length > 0) && (
                  <PanelBadge>
                    {activeAnnotations.length + activeNotes.length}
                  </PanelBadge>
                )}
              </PanelToggle>
            </ToolRight>
          </Toolbar>

          {isPdf && numPages > 1 && !isSplitActive && (
            <ReadProgress
              role="progressbar"
              aria-label="Reading progress"
              aria-valuemin={1}
              aria-valuemax={numPages}
              aria-valuenow={currentPage}
            >
              <ReadProgressFill style={{ width: `${readProgress}%` }} />
            </ReadProgress>
          )}

          {zoomToast !== null && (
            <ZoomToast aria-live="polite">{zoomToast}%</ZoomToast>
          )}

          {shortcutsOpen && (
            <ShortcutsSheet role="dialog" aria-label="Keyboard shortcuts">
              <ShortcutsHead>
                <span>Keyboard &amp; gestures</span>
                <ShortcutsClose
                  onClick={() => setShortcutsOpen(false)}
                  aria-label="Close shortcuts"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </ShortcutsClose>
              </ShortcutsHead>
              <ShortcutList>
                {VIEWER_SHORTCUTS.map(([keys, label]) => (
                  <ShortcutRow key={label}>
                    <span>{label}</span>
                    <span>
                      {keys.map((k) => (
                        <Kbd key={k}>{k}</Kbd>
                      ))}
                    </span>
                  </ShortcutRow>
                ))}
              </ShortcutList>
              <ShortcutFoot>
                On touch screens: pinch to zoom, swipe left/right in page view
                to turn pages.
              </ShortcutFoot>
            </ShortcutsSheet>
          )}

          {noteMode && (
            <NoteBar>
              <span>Click anywhere on the document to place a note.</span>
              <EscKey onClick={() => setNoteModeState(false)}>
                Esc to exit
              </EscKey>
            </NoteBar>
          )}
          {hyperlinkMode && (
            <HyperlinkBar>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span>Click on the document to place a hyperlink marker.</span>
              <EscKey onClick={() => setHyperlinkModeState(false)}>
                Esc to exit
              </EscKey>
            </HyperlinkBar>
          )}
          {redactMode && (
            <RedactBar>
              <span>
                Drag over text or an area to black it out. Redacted text can't
                be selected or copied. You can also select text and choose
                Redact.
              </span>
              {mainRedactions.length > 0 && (
                <RedactBarBtn
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove all ${mainRedactions.length} redactions from this document?`,
                      )
                    )
                      setMainRedactions([]);
                  }}
                >
                  Clear all ({mainRedactions.length})
                </RedactBarBtn>
              )}
              <EscKey onClick={() => setRedactMode(false)}>Esc to exit</EscKey>
            </RedactBar>
          )}
          {isFullscreen && (
            <FullBar>
              Press <strong>Esc</strong> to exit fullscreen
            </FullBar>
          )}

          {/* ── Canvas / scroll area ── */}
          {compareOpen && compareDoc ? (
            <CompareSplitLayout>
              <SplitPane onClick={() => setActiveCompareSide("original")}>
                <SplitPaneHeader
                  style={{
                    background:
                      activeCompareSide === "original" ? "#f0fdf4" : undefined,
                  }}
                >
                  <SplitPaneTitle title={effectiveMainDoc.name}>
                    <strong></strong>{" "}
                    {effectiveMainDoc.name.length > 32
                      ? effectiveMainDoc.name.slice(0, 32) + "…"
                      : effectiveMainDoc.name}
                  </SplitPaneTitle>
                  <SplitPaneRight>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <svg
                        onClick={() => goToPage(currentPage - 1)}
                        style={{
                          cursor: currentPage <= 1 ? "default" : "pointer",
                          opacity: currentPage <= 1 ? 0.3 : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      <PageInput
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={pageInputValue}
                        onChange={handlePageInputChange}
                        onFocus={(e) => e.currentTarget.select()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            commitPageInput();
                            const target = e.currentTarget;
                            setTimeout(() => {
                              if (target) {
                                target.focus();
                                target.select();
                              }
                            }, 0);
                          }
                        }}
                        onBlur={commitPageInput}
                        style={{
                          width: "32px",
                          padding: "2px 0",
                          height: "22px",
                          fontSize: "11px",
                        }}
                      />
                      <PageTotal
                        style={{ fontSize: "11px", color: G.slate500 }}
                      >
                        / {numPages || "—"}
                      </PageTotal>
                      <svg
                        onClick={() => goToPage(currentPage + 1)}
                        style={{
                          cursor:
                            !numPages || currentPage >= numPages
                              ? "default"
                              : "pointer",
                          opacity:
                            !numPages || currentPage >= numPages ? 0.3 : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginLeft: 4,
                      }}
                    >
                      <SplitZoomBtn
                        onClick={() => setZoom((z) => Math.max(30, z - 10))}
                        title="Zoom out"
                      >
                        -
                      </SplitZoomBtn>
                      <span
                        style={{
                          fontSize: 11,
                          color: G.slate500,
                          minWidth: 32,
                          textAlign: "center",
                        }}
                      >
                        {Math.round(zoom)}%
                      </span>
                      <SplitZoomBtn
                        onClick={() => setZoom((z) => Math.min(220, z + 10))}
                        title="Zoom in"
                      >
                        +
                      </SplitZoomBtn>
                    </div>
                    <SplitViewModeBtn onClick={togglePageView}>
                      {singlePageView ? "Scroll View" : "Page View"}
                    </SplitViewModeBtn>
                    <SplitHeaderActionBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectCompareSide("original");
                        setShowSelectCompareModal(true);
                        if (coreCompareDocs.length === 0) {
                          fetchAllCoreDocs();
                        }
                      }}
                    >
                      Change
                    </SplitHeaderActionBtn>
                  </SplitPaneRight>
                </SplitPaneHeader>
                <SplitCanvasArea ref={canvasRef} tabIndex={-1}>
                  <PdfViewer
                    url={effectiveMainDoc.s3url}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    setNumPages={setNumPages}
                    numPages={numPages}
                    zoom={zoom}
                    annotations={annotations}
                    notes={notes}
                    onAddAnnot={addAnnot}
                    onDeleteAnnot={deleteAnnot}
                    onAddNote={addNote}
                    onDeleteNote={deleteNote}
                    noteMode={
                      compareOpen && compareDoc
                        ? activeCompareSide === "original"
                          ? noteMode
                          : false
                        : noteMode
                    }
                    scrollContainerRef={canvasRef}
                    scrollTargetPage={scrollTargetPage}
                    onScrollTargetConsumed={() => setScrollTargetPage(null)}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    isProgrammaticScroll={isProgrammaticScrollRef}
                    singlePageView={singlePageView}
                  />
                </SplitCanvasArea>
              </SplitPane>

              <SplitPane onClick={() => setActiveCompareSide("compare")}>
                <SplitPaneHeader
                  style={{
                    background:
                      activeCompareSide === "compare" ? "#f0fdf4" : undefined,
                  }}
                >
                  <SplitPaneTitle title={compareDoc.name}>
                    <strong></strong>{" "}
                    {compareDoc.name.length > 32
                      ? compareDoc.name.slice(0, 32) + "…"
                      : compareDoc.name}
                  </SplitPaneTitle>
                  <SplitPaneRight>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <svg
                        onClick={() => goToComparePage(comparePage - 1)}
                        style={{
                          cursor: comparePage <= 1 ? "default" : "pointer",
                          opacity: comparePage <= 1 ? 0.3 : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      <PageInput
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={comparePageInputValue}
                        onChange={handleComparePageInputChange}
                        // Match the original viewer: selecting the value lets
                        // a single Backspace clear values such as "11".
                        onFocus={(e) => e.currentTarget.select()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.currentTarget.select();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            commitComparePageInput();
                            const target = e.currentTarget;
                            setTimeout(() => {
                              if (target) {
                                target.focus();
                                target.select();
                              }
                            }, 0);
                          }
                        }}
                        onBlur={(e) => {
                          e.stopPropagation();
                          commitComparePageInput();
                        }}
                        style={{
                          width: "32px",
                          padding: "2px 0",
                          height: "22px",
                          fontSize: "11px",
                        }}
                      />
                      <PageTotal
                        style={{ fontSize: "11px", color: G.slate500 }}
                      >
                        / {compareNumPages || "—"}
                      </PageTotal>
                      <svg
                        onClick={() => goToComparePage(comparePage + 1)}
                        style={{
                          cursor:
                            !compareNumPages || comparePage >= compareNumPages
                              ? "default"
                              : "pointer",
                          opacity:
                            !compareNumPages || comparePage >= compareNumPages
                              ? 0.3
                              : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginLeft: 4,
                      }}
                    >
                      <SplitZoomBtn
                        onClick={() =>
                          setCompareZoom((z) => Math.max(30, z - 10))
                        }
                        title="Zoom out"
                      >
                        -
                      </SplitZoomBtn>
                      <span
                        style={{
                          fontSize: 11,
                          color: G.slate500,
                          minWidth: 32,
                          textAlign: "center",
                        }}
                      >
                        {Math.round(compareZoom)}%
                      </span>
                      <SplitZoomBtn
                        onClick={() =>
                          setCompareZoom((z) => Math.min(220, z + 10))
                        }
                        title="Zoom in"
                      >
                        +
                      </SplitZoomBtn>
                    </div>
                    <SplitViewModeBtn onClick={toggleComparePageView}>
                      {compareSinglePageView ? "Scroll View" : "Page View"}
                    </SplitViewModeBtn>
                    <SplitHeaderActionBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectCompareSide("compare");
                        setShowSelectCompareModal(true);
                        if (coreCompareDocs.length === 0) {
                          fetchAllCoreDocs();
                        }
                      }}
                    >
                      Change
                    </SplitHeaderActionBtn>
                    {/* Removed duplicate Close button from right split header */}
                  </SplitPaneRight>
                </SplitPaneHeader>
                <SplitCanvasArea ref={compareCanvasRef} tabIndex={-1}>
                  <PdfViewer
                    url={compareDoc.s3url}
                    currentPage={comparePage}
                    setCurrentPage={setComparePage}
                    setNumPages={setCompareNumPages}
                    numPages={compareNumPages}
                    zoom={compareZoom}
                    annotations={compareAnnotations}
                    notes={compareNotes}
                    onAddAnnot={addCompareAnnot}
                    onDeleteAnnot={deleteCompareAnnot}
                    onAddNote={addCompareNote}
                    onDeleteNote={deleteCompareNote}
                    noteMode={
                      compareOpen && compareDoc
                        ? activeCompareSide === "compare"
                          ? noteMode
                          : false
                        : false
                    }
                    scrollContainerRef={compareCanvasRef}
                    scrollTargetPage={compareScrollTargetPage}
                    onScrollTargetConsumed={() =>
                      setCompareScrollTargetPage(null)
                    }
                    pageSize={comparePageSize}
                    setPageSize={setComparePageSize}
                    isProgrammaticScroll={isCompareProgrammaticScrollRef}
                    singlePageView={compareSinglePageView}
                  />
                </SplitCanvasArea>
              </SplitPane>
            </CompareSplitLayout>
          ) : (
            <CanvasArea ref={canvasRef} tabIndex={-1}>
              {isPdf && (
                <PdfViewer
                  url={doc.s3url}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  setNumPages={setNumPages}
                  numPages={numPages}
                  zoom={zoom}
                  annotations={annotations}
                  notes={notes}
                  onAddAnnot={addAnnot}
                  onDeleteAnnot={deleteAnnot}
                  onAddNote={addNote}
                  onDeleteNote={deleteNote}
                  noteMode={noteMode}
                  scrollContainerRef={canvasRef}
                  scrollTargetPage={scrollTargetPage}
                  onScrollTargetConsumed={() => setScrollTargetPage(null)}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  isProgrammaticScroll={isProgrammaticScrollRef}
                  singlePageView={singlePageView}
                  hyperlinkMode={hyperlinkMode}
                  hyperlinks={hyperlinks}
                  onAddHyperlink={handleAddHyperlink}
                  onOpenHyperlink={handleOpenLinkedDocHyperlink}
                  onDeleteHyperlink={deleteHyperlink}
                  redactMode={redactMode}
                />
              )}
              {isDocx && (
                <DocxViewer
                  url={doc.s3url}
                  annotations={annotations}
                  notes={notes}
                  onAddAnnot={addAnnot}
                  onDeleteAnnot={deleteAnnot}
                  onAddNote={addNote}
                  onDeleteNote={deleteNote}
                  noteMode={noteMode}
                />
              )}
              {isImage && <ImageViewer url={doc.s3url} name={doc.name} />}
              {isTxt && <TxtViewer url={doc.s3url} />}
              {isVideo && <VideoViewer url={doc.s3url} name={doc.name} />}
              {isAudio && <AudioViewer url={doc.s3url} name={doc.name} />}
              {(isOffice || isLegacyDoc) && <OfficeViewer url={doc.s3url} />}
              {!isPdf &&
                !isDocx &&
                !isImage &&
                !isTxt &&
                !isOffice &&
                !isLegacyDoc &&
                !isVideo &&
                !isAudio && (
                  <ErrorBox>
                    Preview not available for this file type ({ft || "unknown"}
                    ).
                    <br />
                    <a
                      href={doc.s3url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open / Download
                    </a>
                  </ErrorBox>
                )}
            </CanvasArea>
          )}
        </MainArea>

        {/* ── Right Panel ── */}
        {rightOpen && <PanelScrim onClick={() => setRightOpen(false)} />}
        {rightOpen && (
          <RightPanel>
            <SheetGrip aria-hidden="true" />
            <RightPanelTopBar>
              <RightPanelHeading>Document Panel</RightPanelHeading>
              <ClosePanel onClick={() => setRightOpen(false)}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </ClosePanel>
            </RightPanelTopBar>
            <RightSidePanel
              annotations={activeAnnotations}
              notes={activeNotes}
              onDeleteAnnot={activeDeleteAnnot}
              onDeleteNote={activeDeleteNote}
              visibility={
                isCompareActive
                  ? activeDocObj?.visibilityType || "me"
                  : visibility
              }
              onChangeVisibility={changeVisibility}
              doc={activeDocObj}
              numPages={activeNumPages}
              ft={activeFt}
              hideVisibility={compareOpen && compareDoc}
              docHyperlinks={hyperlinks}
              onDeleteDocHyperlink={deleteHyperlink}
              onOpenDocHyperlink={handleOpenLinkedDocHyperlink}
              onUpdateDocHyperlinkNote={updateHyperlinkNote}
            />
          </RightPanel>
        )}
      </ViewerBody>

      {/* ── Select Document to Compare Modal ── */}
      {showSelectCompareModal && (
        <ModalBackdrop onClick={() => setShowSelectCompareModal(false)}>
          <ModalCard
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600 }}
          >
            <ModalHeader>
              <ModalAccent style={{ background: "#00838c" }} />
              <ModalHeaderContent>
                <ModalTitle>
                  {selectCompareSide === "original"
                    ? "Select Original Document"
                    : "Select Document to Split"}
                </ModalTitle>
                <ModalSubtitle>
                  {selectCompareSide === "original"
                    ? "Choose a PDF document from either the Core Bundle or Trial Bundles for the Original side."
                    : "Choose a PDF document from either the Core Bundle or Trial Bundles to Split side-by-side."}
                </ModalSubtitle>
              </ModalHeaderContent>
              <ModalCloseBtn onClick={() => setShowSelectCompareModal(false)}>
                <IcoX size={14} color={G.slate500} />
              </ModalCloseBtn>
            </ModalHeader>
            <ModalBody style={{ padding: "16px 24px" }}>
              <FieldGroup style={{ marginBottom: 15 }}>
                <FieldInput
                  placeholder="Search by Document Name or Tab/Ref..."
                  value={compareSearchQuery}
                  onChange={(e) => setCompareSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${G.slate300}`,
                    borderRadius: 4,
                  }}
                  autoFocus
                />
              </FieldGroup>

              {loadingCompareDocs ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px 0",
                    gap: 10,
                  }}
                >
                  <Spinner $size="24px" />
                  <span style={{ fontSize: 13, color: G.slate500 }}>
                    Loading documents...
                  </span>
                </div>
              ) : (
                <CompareDocList>
                  {/* Core Bundle Section */}
                  {filteredCoreDocs.length > 0 && (
                    <>
                      <CompareDocSectionHeader>
                        Core Bundle Documents
                      </CompareDocSectionHeader>
                      {filteredCoreDocs.map((item) => (
                        <CompareDocItem
                          key={item.id}
                          $active={
                            selectCompareSide === "original"
                              ? effectiveMainDoc?.s3url === item.s3url
                              : compareDoc?.s3url === item.s3url
                          }
                          onClick={() => {
                            if (selectCompareSide === "original") {
                              setMainDocOverride(item);
                              setCurrentPage(1);
                              setPageInputValue(1);
                              setNumPages(null);
                              setZoom(90);
                              setScrollTargetPage(null);
                              setSinglePageView(false);
                              setAnnotations(loadAnnotations(item.id));
                              setNotes(loadNotes(item.id));
                              setShowSelectCompareModal(false);
                              setActiveCompareSide("original");
                            } else {
                              setCompareDoc(item);
                              setComparePage(1);
                              setComparePageInputValue(1);
                              setCompareNumPages(null);
                              setCompareZoom(90);
                              setCompareScrollTargetPage(null);
                              setCompareSinglePageView(false);
                              setCompareAnnotations(loadAnnotations(item.id));
                              setCompareNotes(loadNotes(item.id));
                              setShowSelectCompareModal(false);
                              setCompareOpen(true);
                              setActiveCompareSide("compare");
                            }
                          }}
                        >
                          <CompareDocIcon
                            style={{ background: "#eff6ff", color: "#3b82f6" }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                              <polyline points="13 2 13 9 20 9" />
                            </svg>
                          </CompareDocIcon>
                          <CompareDocInfo>
                            <CompareDocName title={item.name}>
                              {item.name}
                            </CompareDocName>
                            <CompareDocMeta
                              style={{ flexWrap: "wrap", rowGap: "4px" }}
                            >
                              {item.documentNo || item.ref || item.tabRef ? (
                                <DocMetaBadge $bg="#fef3c7" $color="#b45309">
                                  REF:{" "}
                                  {item.documentNo || item.ref || item.tabRef}
                                </DocMetaBadge>
                              ) : null}
                              <DocMetaBadge
                                $bg={getOwnerBadgeStyle(item.ownerType).bg}
                                $color={
                                  getOwnerBadgeStyle(item.ownerType).color
                                }
                              >
                                {getOwnerRoleLabel(item.ownerType)}
                              </DocMetaBadge>
                              <DocMetaBadge
                                $bg={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .bg
                                }
                                $color={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .color
                                }
                              >
                                {
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .label
                                }
                              </DocMetaBadge>
                            </CompareDocMeta>
                          </CompareDocInfo>
                          {item.pageCount ? (
                            <div
                              style={{
                                fontSize: 11,
                                color: G.slate500,
                                marginLeft: "auto",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                paddingLeft: 12,
                              }}
                            >
                              {item.pageCount} page
                              {item.pageCount > 1 ? "s" : ""}
                            </div>
                          ) : null}
                        </CompareDocItem>
                      ))}
                    </>
                  )}

                  {/* Trial Bundle Section */}
                  {filteredTrialDocs.length > 0 && (
                    <>
                      <CompareDocSectionHeader>
                        Trial Bundle Documents
                      </CompareDocSectionHeader>
                      {filteredTrialDocs.map((item) => (
                        <CompareDocItem
                          key={item.id}
                          $active={
                            selectCompareSide === "original"
                              ? effectiveMainDoc?.s3url === item.s3url
                              : compareDoc?.s3url === item.s3url
                          }
                          onClick={() => {
                            if (selectCompareSide === "original") {
                              setMainDocOverride(item);
                              setCurrentPage(1);
                              setPageInputValue(1);
                              setNumPages(null);
                              setZoom(90);
                              setScrollTargetPage(null);
                              setSinglePageView(false);
                              setAnnotations(loadAnnotations(item.id));
                              setNotes(loadNotes(item.id));
                              setShowSelectCompareModal(false);
                              setActiveCompareSide("original");
                            } else {
                              setCompareDoc(item);
                              setComparePage(1);
                              setComparePageInputValue(1);
                              setCompareNumPages(null);
                              setCompareZoom(90);
                              setCompareScrollTargetPage(null);
                              setCompareSinglePageView(false);
                              setCompareAnnotations(loadAnnotations(item.id));
                              setCompareNotes(loadNotes(item.id));
                              setShowSelectCompareModal(false);
                              setCompareOpen(true);
                              setActiveCompareSide("compare");
                            }
                          }}
                        >
                          <CompareDocIcon
                            style={{ background: "#fdf4ff", color: "#d946ef" }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <polygon points="12 2 20 7 20 17 12 22 4 17 4 7 12 2" />
                              <polyline points="12 12 20 7" />
                              <polyline points="12 12 12 22" />
                              <polyline points="12 12 4 7" />
                            </svg>
                          </CompareDocIcon>
                          <CompareDocInfo>
                            <CompareDocName title={item.name}>
                              {item.name}
                            </CompareDocName>
                            <CompareDocMeta
                              style={{ flexWrap: "wrap", rowGap: "4px" }}
                            >
                              <DocMetaBadge $bg="#f5f3ff" $color="#5b21b6">
                                Bundle: {item.bundleName}
                              </DocMetaBadge>
                              <DocMetaBadge $bg="#f0fdfa" $color="#0f766e">
                                Folder: {item.folderName}
                              </DocMetaBadge>
                              <DocMetaBadge $bg="#fef3c7" $color="#b45309">
                                Tab/Ref:{" "}
                                {item.tabRef ||
                                  (item.bundleCode &&
                                  item.tabNum != null &&
                                  item.tabNum !== ""
                                    ? `${item.bundleCode}/${item.tabNum}`
                                    : item.tabNum)}
                              </DocMetaBadge>
                              <DocMetaBadge
                                $bg={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .bg
                                }
                                $color={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .color
                                }
                              >
                                {
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .label
                                }
                              </DocMetaBadge>
                            </CompareDocMeta>
                          </CompareDocInfo>
                          {item.pageCount ? (
                            <div
                              style={{
                                fontSize: 11,
                                color: G.slate500,
                                marginLeft: "auto",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                paddingLeft: 12,
                              }}
                            >
                              {item.pageCount} page
                              {item.pageCount > 1 ? "s" : ""}
                            </div>
                          ) : null}
                        </CompareDocItem>
                      ))}
                    </>
                  )}

                  {filteredCoreDocs.length === 0 &&
                    filteredTrialDocs.length === 0 && (
                      <CompareDocEmpty>
                        No PDF documents found matching your search.
                      </CompareDocEmpty>
                    )}
                </CompareDocList>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setShowSelectCompareModal(false)}>
                Close
              </ModalCancelBtn>
            </ModalFooter>
          </ModalCard>
        </ModalBackdrop>
      )}

      {/* ── Select Target Document for Hyperlink Modal ── */}
      {showHyperlinkDocModal && (
        <ModalBackdrop
          onClick={() => {
            setShowHyperlinkDocModal(false);
            setHyperlinkPending(null);
            setSelectedHlDoc(null);
            setHlTargetPage("");
            setHlPageError("");
          }}
        >
          <ModalCard
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600 }}
          >
            <ModalHeader>
              <ModalAccent style={{ background: G.teal }} />
              <ModalHeaderContent>
                <ModalTitle>
                  {selectedHlDoc
                    ? "Enter Target Page Number"
                    : "Select Target Document"}
                </ModalTitle>
                <ModalSubtitle>
                  {selectedHlDoc
                    ? "Specify the page number in the selected document that this hyperlink will navigate to."
                    : "Choose the document this hyperlink should open. It will appear as a new tab next to the current document."}
                </ModalSubtitle>
              </ModalHeaderContent>
              <ModalCloseBtn
                onClick={() => {
                  setShowHyperlinkDocModal(false);
                  setHyperlinkPending(null);
                  setSelectedHlDoc(null);
                  setHlTargetPage("");
                  setHlPageError("");
                }}
              >
                <IcoX size={14} color={G.slate500} />
              </ModalCloseBtn>
            </ModalHeader>
            <ModalBody style={{ padding: "16px 24px" }}>
              {!selectedHlDoc ? (
                <>
                  <FieldGroup style={{ marginBottom: 15 }}>
                    <FieldInput
                      placeholder="Search by Document Name or Tab/Ref..."
                      value={hyperlinkSearchQuery}
                      onChange={(e) => setHyperlinkSearchQuery(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: `1px solid ${G.slate300}`,
                        borderRadius: 4,
                      }}
                      autoFocus
                    />
                  </FieldGroup>

                  {loadingCompareDocs ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "40px 0",
                        gap: 10,
                      }}
                    >
                      <Spinner $size="24px" />
                      <span style={{ fontSize: 13, color: G.slate500 }}>
                        Loading documents...
                      </span>
                    </div>
                  ) : (
                    <CompareDocList>
                      {/* Core Bundle Section */}
                      {filteredHlCoreDocs.length > 0 && (
                        <>
                          <CompareDocSectionHeader>
                            Core Bundle Documents
                          </CompareDocSectionHeader>
                          {filteredHlCoreDocs.map((item) => (
                            <CompareDocItem
                              key={item.id}
                              $active={false}
                              onClick={() => {
                                if (coreCompareDocs.length === 0)
                                  fetchAllCoreDocs();
                                setSelectedHlDoc(item);
                                setHlTargetPage("1");
                                setHlPageError("");
                              }}
                            >
                              <CompareDocIcon
                                style={{
                                  background: "#eff6ff",
                                  color: "#3b82f6",
                                }}
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                >
                                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                  <polyline points="13 2 13 9 20 9" />
                                </svg>
                              </CompareDocIcon>
                              <CompareDocInfo>
                                <CompareDocName title={item.name}>
                                  {item.name}
                                </CompareDocName>
                                <CompareDocMeta
                                  style={{ flexWrap: "wrap", rowGap: "4px" }}
                                >
                                  {item.documentNo ||
                                  item.ref ||
                                  item.tabRef ? (
                                    <DocMetaBadge
                                      $bg="#fef3c7"
                                      $color="#b45309"
                                    >
                                      REF:{" "}
                                      {item.documentNo ||
                                        item.ref ||
                                        item.tabRef}
                                    </DocMetaBadge>
                                  ) : null}
                                  <DocMetaBadge
                                    $bg={getOwnerBadgeStyle(item.ownerType).bg}
                                    $color={
                                      getOwnerBadgeStyle(item.ownerType).color
                                    }
                                  >
                                    {getOwnerRoleLabel(item.ownerType)}
                                  </DocMetaBadge>
                                  <DocMetaBadge
                                    $bg={
                                      getVisibilityBadgeStyle(
                                        item.visibilityType,
                                      ).bg
                                    }
                                    $color={
                                      getVisibilityBadgeStyle(
                                        item.visibilityType,
                                      ).color
                                    }
                                  >
                                    {
                                      getVisibilityBadgeStyle(
                                        item.visibilityType,
                                      ).label
                                    }
                                  </DocMetaBadge>
                                </CompareDocMeta>
                              </CompareDocInfo>
                              {item.pageCount ? (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: G.slate500,
                                    marginLeft: "auto",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    paddingLeft: 12,
                                  }}
                                >
                                  {item.pageCount} page
                                  {item.pageCount > 1 ? "s" : ""}
                                </div>
                              ) : null}
                            </CompareDocItem>
                          ))}
                        </>
                      )}

                      {/* Trial Bundle Section */}
                      {filteredHlTrialDocs.length > 0 && (
                        <>
                          <CompareDocSectionHeader>
                            Trial Bundle Documents
                          </CompareDocSectionHeader>
                          {filteredHlTrialDocs.map((item) => (
                            <CompareDocItem
                              key={item.id}
                              $active={false}
                              onClick={() => {
                                setSelectedHlDoc(item);
                                setHlTargetPage("1");
                                setHlPageError("");
                              }}
                            >
                              <CompareDocIcon
                                style={{
                                  background: "#fdf4ff",
                                  color: "#d946ef",
                                }}
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                >
                                  <polygon points="12 2 20 7 20 17 12 22 4 17 4 7 12 2" />
                                  <polyline points="12 12 20 7" />
                                  <polyline points="12 12 12 22" />
                                  <polyline points="12 12 4 7" />
                                </svg>
                              </CompareDocIcon>
                              <CompareDocInfo>
                                <CompareDocName title={item.name}>
                                  {item.name}
                                </CompareDocName>
                                <CompareDocMeta
                                  style={{ flexWrap: "wrap", rowGap: "4px" }}
                                >
                                  <DocMetaBadge $bg="#f5f3ff" $color="#5b21b6">
                                    Bundle: {item.bundleName}
                                  </DocMetaBadge>
                                  <DocMetaBadge $bg="#f0fdfa" $color="#0f766e">
                                    Folder: {item.folderName}
                                  </DocMetaBadge>
                                  <DocMetaBadge $bg="#fef3c7" $color="#b45309">
                                    Tab/Ref:{" "}
                                    {item.tabRef ||
                                      (item.bundleCode &&
                                      item.tabNum != null &&
                                      item.tabNum !== ""
                                        ? `${item.bundleCode}/${item.tabNum}`
                                        : item.tabNum)}
                                  </DocMetaBadge>
                                </CompareDocMeta>
                              </CompareDocInfo>
                              {item.pageCount ? (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: G.slate500,
                                    marginLeft: "auto",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    paddingLeft: 12,
                                  }}
                                >
                                  {item.pageCount} page
                                  {item.pageCount > 1 ? "s" : ""}
                                </div>
                              ) : null}
                            </CompareDocItem>
                          ))}
                        </>
                      )}

                      {filteredHlCoreDocs.length === 0 &&
                        filteredHlTrialDocs.length === 0 && (
                          <CompareDocEmpty>
                            No PDF documents found matching your search.
                          </CompareDocEmpty>
                        )}
                    </CompareDocList>
                  )}
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  {/* Selected Document Summary Card */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      background: "#f8fafc",
                      border: `1px solid ${G.slate200}`,
                      borderRadius: 8,
                      padding: "12px 14px",
                    }}
                  >
                    <CompareDocIcon
                      style={
                        selectedHlDoc.bundleName || selectedHlDoc.folderName
                          ? {
                              background: "#fdf4ff",
                              color: "#d946ef",
                              flexShrink: 0,
                            }
                          : {
                              background: "#eff6ff",
                              color: "#3b82f6",
                              flexShrink: 0,
                            }
                      }
                    >
                      {selectedHlDoc.bundleName || selectedHlDoc.folderName ? (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <polygon points="12 2 20 7 20 17 12 22 4 17 4 7 12 2" />
                          <polyline points="12 12 20 7" />
                          <polyline points="12 12 12 22" />
                          <polyline points="12 12 4 7" />
                        </svg>
                      ) : (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                          <polyline points="13 2 13 9 20 9" />
                        </svg>
                      )}
                    </CompareDocIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: G.slate800,
                          marginBottom: 6,
                          wordBreak: "break-word",
                        }}
                      >
                        {selectedHlDoc.name}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        {selectedHlDoc.bundleName && (
                          <DocMetaBadge $bg="#f5f3ff" $color="#5b21b6">
                            Bundle: {selectedHlDoc.bundleName}
                          </DocMetaBadge>
                        )}
                        {selectedHlDoc.folderName && (
                          <DocMetaBadge $bg="#f0fdfa" $color="#0f766e">
                            Folder: {selectedHlDoc.folderName}
                          </DocMetaBadge>
                        )}
                        {(selectedHlDoc.tabRef ||
                          (selectedHlDoc.bundleCode &&
                            selectedHlDoc.tabNum != null &&
                            selectedHlDoc.tabNum !== "")) && (
                          <DocMetaBadge $bg="#fef3c7" $color="#b45309">
                            Tab/Ref:{" "}
                            {selectedHlDoc.tabRef ||
                              `${selectedHlDoc.bundleCode}/${selectedHlDoc.tabNum}`}
                          </DocMetaBadge>
                        )}
                        {!selectedHlDoc.bundleName &&
                          (selectedHlDoc.documentNo ||
                            selectedHlDoc.ref ||
                            selectedHlDoc.tabRef) && (
                            <DocMetaBadge $bg="#fef3c7" $color="#b45309">
                              REF:{" "}
                              {selectedHlDoc.documentNo ||
                                selectedHlDoc.ref ||
                                selectedHlDoc.tabRef}
                            </DocMetaBadge>
                          )}
                        {selectedHlDoc.pageCount ? (
                          <DocMetaBadge $bg="#e0f2fe" $color="#0369a1">
                            {selectedHlDoc.pageCount} page
                            {selectedHlDoc.pageCount > 1 ? "s" : ""}
                          </DocMetaBadge>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Target Page Number Input */}
                  <FieldGroup>
                    <label
                      htmlFor="hyperlink-target-page-input"
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 600,
                        color: G.slate700,
                        marginBottom: 4,
                      }}
                    >
                      Target Page Number{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div
                      style={{
                        fontSize: 11,
                        color: G.slate500,
                        marginBottom: 8,
                      }}
                    >
                      {selectedHlDoc.pageCount
                        ? `Enter a page number between 1 and ${selectedHlDoc.pageCount}`
                        : "Enter a positive whole number (e.g. 1, 2, 3...)"}
                    </div>
                    <FieldInput
                      id="hyperlink-target-page-input"
                      type="number"
                      min="1"
                      max={selectedHlDoc.pageCount || undefined}
                      step="1"
                      placeholder="Enter page number (e.g. 1)"
                      value={hlTargetPage}
                      onChange={(e) => {
                        const val = e.target.value;
                        setHlTargetPage(val);
                        if (hlPageError) {
                          setHlPageError(
                            validatePageNumber(val, selectedHlDoc.pageCount),
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const err = validatePageNumber(
                            hlTargetPage,
                            selectedHlDoc.pageCount,
                          );
                          if (err) {
                            setHlPageError(err);
                          } else {
                            confirmHyperlink(selectedHlDoc, hlTargetPage);
                          }
                        }
                      }}
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        border: `1.5px solid ${hlPageError ? "#ef4444" : G.slate300}`,
                        borderRadius: 6,
                        fontSize: 13,
                        boxSizing: "border-box",
                      }}
                      autoFocus
                    />
                    {hlPageError && (
                      <div
                        style={{
                          color: "#ef4444",
                          fontSize: 12,
                          marginTop: 6,
                          fontWeight: 500,
                        }}
                      >
                        {hlPageError}
                      </div>
                    )}
                  </FieldGroup>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              {selectedHlDoc && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedHlDoc(null);
                    setHlPageError("");
                  }}
                  style={{
                    marginRight: "auto",
                    background: "transparent",
                    border: `1px solid ${G.slate300}`,
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 500,
                    color: G.slate700,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  ← Change Document
                </button>
              )}
              <ModalCancelBtn
                onClick={() => {
                  setShowHyperlinkDocModal(false);
                  setHyperlinkPending(null);
                  setSelectedHlDoc(null);
                  setHlTargetPage("");
                  setHlPageError("");
                }}
              >
                Cancel
              </ModalCancelBtn>
              {selectedHlDoc && (
                <button
                  type="button"
                  onClick={() => {
                    const err = validatePageNumber(
                      hlTargetPage,
                      selectedHlDoc.pageCount,
                    );
                    if (err) {
                      setHlPageError(err);
                    } else {
                      confirmHyperlink(selectedHlDoc, hlTargetPage);
                    }
                  }}
                  style={{
                    background: `linear-gradient(135deg, ${G.teal}, ${G.tealDark})`,
                    color: "#ffffff",
                    border: "none",
                    borderRadius: 6,
                    padding: "7px 18px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0, 131, 140, 0.25)",
                    transition: "all 0.15s",
                  }}
                >
                  Create Hyperlink
                </button>
              )}
            </ModalFooter>
          </ModalCard>
        </ModalBackdrop>
      )}
    </AppWrapper>
  );
}

// ─── Compare View Styled Components ───────────────────────────────────────────

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: ${(props) => props.$size || "20px"};
  height: ${(props) => props.$size || "20px"};
  border: 2px solid ${G.slate200};
  border-top-color: ${G.teal};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const CompareBtn = styled.button`
  background: ${(props) => (props.$active ? G.teal : "#ffffff")};
  color: ${(props) => (props.$active ? "#ffffff" : G.teal)};
  border: 1.5px solid ${G.teal};
  border-radius: 7px;
  width: 32px;
  height: 30px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: ${(props) => (props.$active ? G.tealDark : G.tealGhost)};
  }
`;

const CompareDocList = styled.div`
  max-height: 350px;
  overflow-y: auto;
  border: 1px solid ${G.slate200};
  border-radius: 6px;
  margin-top: 10px;
  background: ${G.slate50};
`;

const CompareDocSectionHeader = styled.div`
  background: ${G.slate100};
  padding: 8px 12px;
  font-weight: 600;
  font-size: 11px;
  color: ${G.slate600};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-bottom: 1px solid ${G.slate200};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const CompareDocItem = styled.div`
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-bottom: 1px solid ${G.slate100};
  transition: all 0.15s ease-in-out;
  background: ${(props) => (props.$active ? G.tealLight : "white")};

  &:hover {
    background: ${(props) => (props.$active ? G.tealLight : G.slate50)};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CompareDocIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 4px;
  background: ${G.tealLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${G.teal};
  flex-shrink: 0;
`;

const CompareDocInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const CompareDocName = styled.div`
  font-size: 13px;
  color: #293461;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompareDocMeta = styled.div`
  font-size: 11px;
  color: ${G.slate500};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DocMetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  background: ${(props) => props.$bg || G.slate100};
  color: ${(props) => props.$color || G.slate600};
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  text-transform: capitalize;
  font-family: inherit;
`;

const CompareDocEmpty = styled.div`
  padding: 20px;
  text-align: center;
  color: ${G.slate400};
  font-size: 13px;
`;

const CompareSplitLayout = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  overflow: hidden;
  background: #f1f5f9;
  min-height: 0;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SplitPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
  min-width: 0;

  &:first-child {
    border-right: 2px solid ${G.slate200};
  }
  @media (max-width: 768px) {
    height: 50%;
    &:first-child {
      border-right: none;
      border-bottom: 2px solid ${G.slate200};
    }
  }
`;

const SplitPaneHeader = styled.div`
  background: ${G.slate50};
  border-bottom: 1px solid ${G.slate200};
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  box-sizing: border-box;
  flex-shrink: 0;
  @media (max-width: 768px) {
    height: auto;
    min-height: 40px;
    padding: 6px 10px;
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const SplitPaneTitle = styled.div`
  font-size: 12px;
  color: #293461;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 45%;
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SplitPaneRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 768px) {
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const SplitZoomBtn = styled.button`
  background: transparent;
  border: 1px solid ${G.slate300};
  border-radius: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  color: ${G.slate600};
  transition: all 0.15s;

  &:hover {
    background: ${G.slate100};
    border-color: ${G.slate400};
  }
`;

const SplitHeaderActionBtn = styled.button`
  background: ${(props) => (props.$danger ? "transparent" : G.teal)};
  color: ${(props) => (props.$danger ? G.red : "#ffffff")};
  border: 1px solid ${(props) => (props.$danger ? G.red : G.teal)};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) => (props.$danger ? G.redLight : G.tealDark)};
    color: ${(props) => (props.$danger ? G.redDark : "#ffffff")};
  }
`;

const SplitCanvasArea = styled.div`
  flex: 1;
  background: #f1f5f9;
  overflow-y: auto;
  outline: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
  scroll-behavior: auto;
  width: 100%;
  height: 100%;
`;

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const sheetUp = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`;

const toastPop = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.92); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const shimmerMove = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,129,138,0.18); }
  50%       { box-shadow: 0 0 0 5px rgba(0,129,138,0); }
`;

const PlaceholderWrap = styled.div`
  background: #ffffff;
  border-radius: 3px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

const PlaceholderInner = styled.div`
  padding: 48px 52px;
`;

const SkeletonShimmer = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.7) 50%,
    transparent 100%
  );
  animation: ${shimmerMove} 1.6s ease infinite;
  z-index: 1;
`;

const DocxSkeletonWrap = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 840px;
  border-radius: 3px;
  position: relative;
  overflow: hidden;
`;

const DocxSkeletonInner = styled.div`
  padding: 60px 72px;
`;

const SkeletonBar = styled.div`
  background: #e8edf3;
  border-radius: 4px;
`;

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  background: #e9eef6;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #1e293b;
  overflow: hidden;
  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      background: #0f172a;
    `}
`;

const TabBar = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #dde6f0;
  height: 48px;
  flex-shrink: 0;
  padding: 0 14px;
  box-shadow: 0 1px 8px rgba(15, 23, 42, 0.06);
  @media (max-width: 640px) {
    height: 46px;
    padding: 0 8px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  padding: 5px 10px;
  border-radius: 7px;
  white-space: nowrap;
  flex-shrink: 0;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
  @media (pointer: coarse) {
    min-height: 36px;
  }
`;

const BackLabel = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const TabDivider = styled.div`
  width: 1px;
  height: 20px;
  background: #e2e8f0;
  flex-shrink: 0;
  margin: 0 10px;
`;

const TabScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  overflow-x: auto;
  flex: 1;
  &::-webkit-scrollbar {
    height: 0;
  }
`;

const DocTab = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#ecfeff" : "transparent")};
  border: 1px solid ${({ $active }) => ($active ? "#a5f3fc" : "transparent")};
  font-size: 12px;
  color: ${({ $active }) => ($active ? "#0f172a" : "#64748b")};
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 220px;
  transition: all 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
  @media (max-width: 640px) {
    max-width: 170px;
    padding: 7px 9px;
  }
`;

const TabFilePill = styled.span`
  font-size: 9px;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 4px;
  flex-shrink: 0;
  background: ${({ $ft }) => ($ft === "pdf" ? "#fee2e2" : "#dbeafe")};
  color: ${({ $ft }) => ($ft === "pdf" ? "#dc2626" : "#2563eb")};
`;

const TabName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
`;

const TabCloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #cbd5e1;
  padding: 2px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
`;

const ViewerBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  position: relative;
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  position: relative;
`;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #dfe8f2;
  padding: 8px 16px;
  gap: 8px;
  flex-shrink: 0;
  min-height: 52px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
  z-index: 20;
  box-sizing: border-box;
  width: 100%;
  backdrop-filter: saturate(160%) blur(6px);

  @media (max-width: 900px) {
    display: flex;
    flex-wrap: wrap;
    row-gap: 6px;
    padding: 8px 10px;
  }
`;

const ToolLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  justify-self: start;
  min-width: 0;
  @media (max-width: 900px) {
    flex-shrink: 1;
  }
`;

const ToolRight = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  justify-self: end;
  min-width: 0;
  margin-left: auto;
  @media (pointer: coarse) {
    gap: 6px;
    & > button,
    & > div > button {
      min-width: 36px;
      min-height: 34px;
    }
  }
`;

const ToolDivider = styled.div`
  width: 1px;
  height: 18px;
  background: #e8edf3;
  flex-shrink: 0;
  margin: 0 1px;
`;

const PageNav = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5px;
  background: #f8fbfd;
  border: 1px solid #dbe5ef;
  border-radius: 8px;
  padding: 2px 3px;
  @media (pointer: coarse) {
    & > button {
      width: 32px;
      height: 32px;
    }
    & > input {
      width: 38px;
      height: 30px;
      font-size: 16px;
    }
  }
`;

const ViewModeBtn = styled.button`
  width: 30px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #e8edf3;
  border-radius: 7px;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    background: #e6f7f8;
    border-color: #00818a;
    color: #00717a;
  }
`;

const PageBtn = styled.button`
  width: 23px;
  height: 23px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  border-radius: 4px;
  padding: 0;
  transition:
    background 0.15s,
    color 0.15s;
  &:hover:not(:disabled) {
    background: #e6f7f8;
    color: #00818a;
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
  ${({ $secondary }) =>
    $secondary &&
    css`
      @media (max-width: 600px) {
        display: none;
      }
    `}
`;

const PageInput = styled.input`
  width: 30px;
  height: 23px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  outline: none;
  color: #1e293b;
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-align: center;
  padding: 0;
  box-sizing: border-box;
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }
  &:focus {
    border-color: #00818a;
    box-shadow: 0 0 0 2px rgba(0, 129, 138, 0.12);
  }
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
`;

const PageTotal = styled.span`
  font-size: 10.5px;
  color: #94a3b8;
  white-space: nowrap;
  padding: 0 2px;
`;

const ZoomGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  background: #f8fbfd;
  border: 1px solid #dbe5ef;
  border-radius: 8px;
  padding: 2px;
`;

const ZoomBtn = styled.button`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: #ffffff;
    color: #00818a;
    border-color: #c8edf0;
    box-shadow: 0 2px 7px rgba(15, 23, 42, 0.08);
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
  @media (pointer: coarse) {
    width: 32px;
    height: 32px;
  }
`;

const ZoomWrap = styled.div`
  position: relative;
`;

const ZoomLabel = styled.button`
  background: #ffffff;
  border: 1px solid #dbe5ef;
  border-radius: 6px;
  color: #1e293b;
  font-size: 11.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  padding: 2px 6px;
  min-width: 48px;
  height: 25px;
  text-align: center;
  transition: all 0.15s;
  &:hover {
    background: #ecfeff;
    border-color: #67dce3;
    color: #006a72;
  }
`;

const ZoomDropdown = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  max-height: min(60vh, 420px);
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
  min-width: 160px;
  z-index: 600;
  overflow: hidden;
  animation: ${fadeUp} 0.15s ease;
`;

const ZoomItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 9px 14px;
  font-size: 12.5px;
  border: none;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontRegular};
  transition: background 0.12s;
  background: ${({ $active }) => ($active ? "#f0fafa" : "transparent")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const DocTitleArea = styled.div`
  text-align: center;
  min-width: 0;
  max-width: 100%;
  padding: 0 10px;
  justify-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 900px) {
    order: -1;
    flex: 1 1 100%;
    align-items: flex-start;
    text-align: left;
    padding: 0 2px;
  }
`;

const DocTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const DocSubtitle = styled.div`
  font-size: 10px;
  color: #94a3b8;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const NoteModeToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  border: 1.5px solid ${({ $active }) => ($active ? "#f59e0b" : "#e8edf3")};
  border-radius: 7px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#fffbeb" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#d97706" : "#64748b")};
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover {
    background: #fffbeb;
    border-color: #f59e0b;
    color: #d97706;
  }
`;

const DlWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const DownloadTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  background: linear-gradient(135deg, #00818a, #006a72);
  color: #fff;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 129, 138, 0.22);
  transition: all 0.15s;
  &:hover {
    background: linear-gradient(135deg, #006a72, #00575e);
    box-shadow: 0 4px 12px rgba(0, 129, 138, 0.32);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
`;

const DlDropdown = styled.div`
  position: absolute;
  top: calc(100% + 7px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.13);
  min-width: 260px;
  max-width: calc(100vw - 24px);
  z-index: 600;
  overflow: hidden;
  animation: ${fadeUp} 0.15s ease;
`;

const DlItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  font-family: ${theme.fonts.primaryFontRegular};
  padding: 13px 16px;
  font-size: 12.5px;
  border: none;
  background: none;
  cursor: pointer;
  color: #475569;
  transition: background 0.12s;
  &:hover {
    background: #f8fafc;
    color: #1e293b;
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
  & + & {
    border-top: 1px solid #f1f5f9;
  }
`;

const DlItemTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
`;

const DlItemSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`;

const IconBtn = styled.button`
  width: 32px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${({ $active }) => ($active ? "#00818a" : "#e8edf3")};
  border-radius: 7px;
  background: ${({ $active }) => ($active ? "#f0fafa" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
    border-color: #c8d3df;
  }
`;

const PanelToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1.5px solid ${({ $active }) => ($active ? "#00818a" : "#e8edf3")};
  border-radius: 7px;
  padding: 5px 9px;
  background: ${({ $active }) => ($active ? "#f0fafa" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
  &:hover {
    background: #f0fafa;
    border-color: #00818a55;
    color: #00818a;
  }
`;

const PanelBadge = styled.span`
  background: #00818a;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 5px;
  min-width: 16px;
  text-align: center;
`;

const NoteBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
  padding: 7px 18px;
  font-size: 12px;
  color: #92400e;
  flex-shrink: 0;
  animation: ${fadeUp} 0.18s ease;
`;

const EscKey = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 5px;
  padding: 2px 9px;
  font-size: 11px;
  cursor: pointer;
  color: #92400e;
  font-family: ${theme.fonts.primaryFontSemiBold};
  &:hover {
    background: #fde68a;
  }
`;

const FullBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.8);
  padding: 5px;
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
  strong {
    color: #e2e8f0;
  }
`;

const CanvasArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  background:
    radial-gradient(
      circle at top left,
      rgba(0, 129, 138, 0.08),
      transparent 28%
    ),
    linear-gradient(180deg, #e6edf6 0%, #d7e1ed 100%);
  padding: 12px 20px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  touch-action: pan-x pan-y;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #b4c0d0;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  &:focus {
    outline: none;
  }
  @media (max-width: 640px) {
    padding: 10px 8px 10px;
  }
`;

const PdfWrap = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  user-select: ${({ $noteMode, $hyperlinkMode, $redactMode }) =>
    $noteMode || $hyperlinkMode || $redactMode ? "none" : "text"};
  cursor: ${({ $noteMode, $hyperlinkMode, $redactMode }) =>
    $hyperlinkMode
      ? "cell"
      : $noteMode || $redactMode
        ? "crosshair"
        : "text"};
  position: relative;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 20px 48px rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
`;

const PdfList = styled.div`
  position: relative;
  margin: 0 auto;
`;

const PdfPageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 22px;
  background: #fff;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: 3px;
  overflow: visible;
  &:last-child {
    margin-bottom: 0;
  }
  /* Text-only react-pdf page: sized like the canvas so the text layer lines up. */
  .dv-pdf-text-page {
    width: 100%;
    height: 100%;
  }
`;

const DocxScrollArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DocxContent = styled.div`
  background: #fff;
  width: 100%;
  max-width: 840px;
  padding: 64px 72px;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 20px 48px rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  animation: ${fadeUp} 0.3s ease;
  line-height: 1.9;
  font-size: 14.5px;
  color: #1a1a2e;
  user-select: ${({ $noteMode }) => ($noteMode ? "none" : "text")};
  cursor: ${({ $noteMode }) => ($noteMode ? "crosshair" : "text")};
  position: relative;
  letter-spacing: 0.01em;
  @media (max-width: 640px) {
    padding: 32px 24px;
    font-size: 13px;
  }
  h1,
  h2,
  h3,
  h4 {
    color: #1a237e;
    margin: 1.4em 0 0.6em;
  }
  h1 {
    font-size: 1.55em;
    font-weight: 700;
    border-bottom: 2px solid #e8edf5;
    padding-bottom: 0.3em;
  }
  h2 {
    font-size: 1.25em;
    font-weight: 600;
  }
  p {
    margin-bottom: 1em;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1.2em 0;
  }
  th,
  td {
    border: 1px solid #dde3ec;
    padding: 9px 13px;
    text-align: left;
  }
  th {
    background: #f5f7fb;
    font-weight: 600;
    color: #374151;
  }
  tr:hover td {
    background: #fafbfd;
  }
  ul,
  ol {
    padding-left: 1.8em;
    margin-bottom: 1em;
  }
  li {
    margin-bottom: 0.3em;
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }
  blockquote {
    border-left: 4px solid #00818a;
    margin: 1.2em 0;
    padding: 0.8em 1.2em;
    background: #f0fafa;
    border-radius: 0 6px 6px 0;
    color: #374151;
  }
  ::selection {
    background: rgba(0, 129, 138, 0.15);
  }
  mark:hover {
    filter: brightness(0.9);
  }
`;

const RightPanel = styled.aside`
  position: absolute;
  top: 56px;
  bottom: 0;
  right: 0;
  z-index: 30;
  width: 320px;
  background: #ffffff;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: -4px 0 16px rgba(15, 23, 42, 0.08);
  animation: ${slideIn} 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  @media (max-width: 900px) {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: min(78vh, 640px);
    border-left: none;
    border-radius: 18px 18px 0 0;
    z-index: 1200;
    box-shadow: 0 -16px 40px rgba(15, 23, 42, 0.22);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    animation: ${sheetUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const PanelScrim = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1190;
    background: rgba(15, 23, 42, 0.4);
    backdrop-filter: blur(2px);
    animation: ${fadeUp} 0.2s ease;
  }
`;

const SheetGrip = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: 4px;
    background: #cbd5e1;
    margin: 8px auto 2px;
    flex-shrink: 0;
  }
`;

const RightPanelTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e8edf3;
  flex-shrink: 0;
  background: #fafbfc;
`;

const RightPanelHeading = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: 0.3px;
`;

const ClosePanel = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  border-radius: 5px;
  transition:
    background 0.15s,
    color 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

const RightPanelWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const PanelTabStrip = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8edf3;
  background: #ffffff;
  flex-shrink: 0;
  padding: 0 4px;
`;

const PanelTabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px 8px 9px;
  font-size: 11.5px;
  font-family: ${({ $active }) =>
    $active ? theme.fonts.primaryFontSemiBold : theme.fonts.primaryFontRegular};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  background: transparent;
  border: none;
  border-bottom: 2.5px solid
    ${({ $active }) => ($active ? "#00818a" : "transparent")};
  cursor: pointer;
  white-space: nowrap;
  flex: 1;
  text-align: center;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ $active }) => ($active ? "#006a72" : "#1e293b")};
    background: ${({ $active }) => ($active ? "transparent" : "#f8fafc")};
  }
`;

const PanelTabBadge = styled.span`
  background: ${({ $active }) => ($active ? "#00818a18" : "#f1f5f9")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  min-width: 16px;
  text-align: center;
  line-height: 1.2;
`;

const HyperlinkCard = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 10px 12px;
  text-decoration: none;
  color: inherit;
  animation: ${fadeUp} 0.2s ease;
  transition:
    box-shadow 0.15s,
    transform 0.15s,
    border-color 0.15s;
  &:hover {
    box-shadow: 0 3px 12px rgba(0, 122, 255, 0.1);
    border-color: #bee3f8;
    transform: translateY(-1px);
  }
`;

const HyperlinkIconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #ebf8ff;
  color: #007aff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HyperlinkInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HyperlinkTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  font-family: ${theme.fonts.primaryFontSemiBold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HyperlinkUrl = styled.div`
  font-size: 10.5px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HyperlinkExternalIcon = styled.div`
  color: #94a3b8;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const RightPanelScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  padding: 0 0 16px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const SectionBlock = styled.div`
  padding: 18px 16px;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: #f0f4f8;
  margin: 0 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const SectionBadge = styled.span`
  background: ${({ $color }) => $color + "18"};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color + "30"};
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  min-width: 22px;
  text-align: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 12px 8px;
  gap: 6px;
`;

const EmptyTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AnnotCard = styled.div`
  display: flex;
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  overflow: hidden;
  animation: ${fadeUp} 0.2s ease;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  &:hover {
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const AnnotColorStrip = styled.div`
  width: 4px;
  flex-shrink: 0;
`;

const AnnotCardBody = styled.div`
  flex: 1;
  padding: 10px 11px;
  min-width: 0;
`;

const AnnotCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 5px;
`;

const AnnotSnippet = styled.div`
  font-size: 11.5px;
  color: #475569;
  font-style: italic;
  line-height: 1.45;
  flex: 1;
  min-width: 0;
`;

const AnnotComment = styled.div`
  font-size: 11px;
  color: #059669;
  background: #f0fdf4;
  border-radius: 5px;
  padding: 4px 7px;
  margin-bottom: 6px;
  line-height: 1.45;
`;

const AnnotFooter = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const FooterChip = styled.span`
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 4px;
  font-weight: 500;
  background: ${({ $color, $yellow }) =>
    $yellow ? "#fef3c7" : $color ? $color + "20" : "#f1f5f9"};
  color: ${({ $color, $yellow }) =>
    $yellow ? "#92400e" : $color || "#64748b"};
`;

const FooterTime = styled.span`
  font-size: 10px;
  color: #94a3b8;
  margin-left: auto;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #cbd5e1;
  padding: 3px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
`;

const NoteCard = styled.div`
  background: linear-gradient(140deg, #fffdf0, #fef9c3);
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 11px 12px;
  animation: ${fadeUp} 0.2s ease;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  &:hover {
    box-shadow: 0 3px 12px rgba(245, 158, 11, 0.14);
    transform: translateY(-1px);
  }
`;

const NoteCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
`;

const NoteCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const NoteCardText = styled.div`
  font-size: 12px;
  color: #713f12;
  line-height: 1.6;
`;

const InfoList = styled.div`
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
  transition: background 0.12s;
  &:hover {
    background: #fafbfc;
  }
`;

const InfoKey = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  min-width: 80px;
  flex-shrink: 0;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const InfoVal = styled.div`
  font-size: 12px;
  color: #475569;
  word-break: break-all;
  flex: 1;
`;

const FormatPill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 800;
  background: ${({ $ft }) => ($ft === "pdf" ? "#fee2e2" : "#dbeafe")};
  color: ${({ $ft }) => ($ft === "pdf" ? "#dc2626" : "#2563eb")};
`;

const StatBadge = styled.span`
  display: inline-block;
  background: ${({ $color }) => $color + "18"};
  color: ${({ $color }) => $color};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  min-width: 26px;
  text-align: center;
`;

const ShareDesc = styled.div`
  font-size: 11.5px;
  color: #64748b;
  margin-bottom: 12px;
  line-height: 1.55;
`;

const VisOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VisOptionCard = styled.button`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 13px;
  background: ${({ $active }) => ($active ? "#f0fafa" : "#ffffff")};
  border: 1.5px solid ${({ $active }) => ($active ? "#00818a" : "#e2e8f0")};
  border-radius: 10px;
  width: 100%;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  ${({ $active }) =>
    $active &&
    css`
      animation: ${pulseGlow} 2.5s ease infinite;
    `}
  &:hover {
    background: #f0fafa;
    border-color: #00818a66;
    transform: translateX(1px);
  }
`;

const VisIconCircle = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const VisOptionText = styled.div`
  flex: 1;
  min-width: 0;
`;

const VisOptionName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const VisOptionDesc = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.4;
`;

const VisRadioCircle = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ $active }) => ($active ? "#00818a" : "#cbd5e1")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s;
`;

const VisRadioDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00818a;
`;

const TipBox = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
  min-width: 270px;
  animation: ${fadeUp} 0.15s ease;
`;

const TipLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  margin-bottom: 9px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const TipPreview = styled.div`
  font-size: 11px;
  color: #475569;
  margin-bottom: 12px;
  font-style: italic;
  line-height: 1.45;
  border-left: 3px solid #f59e0b;
  padding-left: 9px;
`;

const TipColorRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const TipColorDot = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  background: ${({ $bg }) => $bg};
  border: 2.5px solid
    ${({ $selected }) => ($selected ? "#1e293b" : "transparent")};
  box-shadow: ${({ $selected }) => ($selected ? "0 0 0 2px #cbd5e1" : "none")};
  transition: transform 0.15s;
  &:hover {
    transform: scale(1.2);
  }
`;

const TipInput = styled.input`
  width: 100%;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 12px;
  color: #1e293b;
  margin-bottom: 11px;
  font-family: ${theme.fonts.primaryFontRegular};
  box-sizing: border-box;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: none;
    border-color: #00818a;
    box-shadow: 0 0 0 3px rgba(0, 129, 138, 0.1);
  }
`;

const NoteInputTA = styled.textarea`
  width: 100%;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  padding: 9px 10px;
  font-size: 12px;
  color: #1e293b;
  font-family: ${theme.fonts.primaryFontRegular};
  resize: vertical;
  box-sizing: border-box;
  margin-bottom: 7px;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const TipActions = styled.div`
  display: flex;
  gap: 7px;
  justify-content: flex-end;
`;

const TipSave = styled.button`
  background: linear-gradient(135deg, #00818a, #006a72);
  color: #fff;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border: none;
  border-radius: 7px;
  padding: 7px 14px;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 129, 138, 0.2);
  transition: all 0.15s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 129, 138, 0.3);
    transform: translateY(-1px);
  }
`;

const TipRedact = styled.button`
  background: #1e293b;
  color: #fff;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border: none;
  border-radius: 7px;
  padding: 7px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #000000;
  }
`;

const TipCancel = styled.button`
  background: transparent;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
  padding: 7px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

const NotePin = styled.button`
  background: #fffbeb;
  border: 1.5px solid #f59e0b;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
  padding: 0;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 4px 14px rgba(245, 158, 11, 0.32);
  }
`;

const StickyCard = styled.div`
  position: absolute;
  left: 32px;
  top: 0;
  background: linear-gradient(135deg, #fefce8, #fef9c3);
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  padding: 11px 13px;
  min-width: 190px;
  max-width: 240px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  z-index: 200;
  animation: ${fadeUp} 0.15s ease;
`;

const StickyText = styled.div`
  font-size: 12px;
  color: #713f12;
  line-height: 1.5;
  margin-bottom: 7px;
`;
const StickyMeta = styled.div`
  font-size: 10px;
  color: #a16207;
  margin-bottom: 7px;
`;
const StickyDelete = styled.button`
  background: #fee2e2;
  color: #ef4444;
  border: 1px solid #fecaca;
  border-radius: 5px;
  padding: 3px 9px;
  font-size: 11px;
  cursor: pointer;
  &:hover {
    background: #fecaca;
  }
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 24px 32px;
  font-size: 13px;
  max-width: 480px;
  text-align: center;
  line-height: 1.7;
  margin: 40px auto;
  a {
    color: #2563eb;
  }
`;

const TruncatedText = styled.div`
  color: #475569;
  font-size: 14px;
  line-height: 1.7;
  text-align: justify;

  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $expanded }) => ($expanded ? "unset" : 4)};
  overflow: hidden;

  margin-bottom: 8px;
`;

const ExpandLink = styled.button`
  background: none;
  border: none;
  color: #00818a;
  font-size: 11px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
  display: block;
  &:hover {
    color: #006a72;
    text-decoration: none;
  }
`;

const ImageViewerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const MediaViewerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
`;

const VideoCard = styled.div`
  background: #0f172a;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  border: 1px solid #1e293b;
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MediaTitle = styled.div`
  color: #f1f5f9;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AudioCard = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
`;

const pulse = keyframes`
  0% { transform: scale(0.96); opacity: 0.8; }
  50% { transform: scale(1.04); opacity: 1; box-shadow: 0 8px 24px rgba(0, 129, 138, 0.25); }
  100% { transform: scale(0.96); opacity: 0.8; }
`;

const AudioDisc = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00818a, #006a72);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 129, 138, 0.15);
  animation: ${pulse} 3s ease-in-out infinite;
`;

const AudioTitle = styled.div`
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TxtContent = styled.div`
  background: #fff;
  width: 100%;
  max-width: 840px;
  padding: 48px 56px;
  border-radius: 3px;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 20px 48px rgba(0, 0, 0, 0.08);
  pre {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ${theme.fonts.primaryFontRegular};
    font-size: 13px;
    line-height: 1.7;
    color: #1e293b;
  }
`;

const OfficeFrameWrap = styled.div`
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12);
`;

const SplitViewModeBtn = styled.button`
  height: 22px;
  padding: 0 7px;
  border: 1px solid ${G.slate300};
  border-radius: 4px;
  background: #ffffff;
  color: ${G.slate600};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${G.tealLight};
    border-color: ${G.teal};
    color: ${G.teal};
  }
`;

// ─── Hyperlink Feature Styled Components ──────────────────────────────────────

const HyperlinkModeToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  border: 1.5px solid ${({ $active }) => ($active ? G.teal : "#e8edf3")};
  border-radius: 7px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? G.tealLight : "#ffffff")};
  color: ${({ $active }) => ($active ? G.teal : "#64748b")};
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover {
    background: ${G.tealLight};
    border-color: ${G.teal};
    color: ${G.teal};
  }
`;

const HyperlinkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  background: ${G.tealLight};
  border-bottom: 1px solid ${G.tealMid};
  padding: 7px 18px;
  font-size: 12px;
  color: ${G.tealDark};
  flex-shrink: 0;
  animation: ${fadeUp} 0.18s ease;
`;

const RedactBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  padding: 7px 18px;
  font-size: 12px;
  color: #7f1d1d;
  flex-shrink: 0;
  animation: ${fadeUp} 0.18s ease;
`;

const RedactBarBtn = styled.button`
  border: 1px solid #fca5a5;
  background: #ffffff;
  color: #b91c1c;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 11.5px;
  cursor: pointer;
  &:hover {
    background: #fee2e2;
  }
`;

const HyperlinkPinBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${G.teal}, ${G.tealDark});
  border: 2px solid #ffffff;
  box-shadow: 0 2px 8px rgba(0, 131, 140, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #ffffff;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
  position: relative;
  &:hover {
    transform: scale(1.18);
    box-shadow: 0 4px 14px rgba(0, 131, 140, 0.55);
  }
`;

const HyperlinkPinCard = styled.div`
  position: absolute;
  ${(props) =>
    props.$placementX === "left"
      ? css`
          right: 34px;
          left: auto;
        `
      : css`
          left: 34px;
          right: auto;
        `}
  ${(props) => {
    if (props.$placementY === "bottom") {
      return css`
        bottom: 0;
        top: auto;
        transform: none;
      `;
    }
    if (props.$placementY === "top") {
      return css`
        top: 0;
        bottom: auto;
        transform: none;
      `;
    }
    return css`
      top: 50%;
      bottom: auto;
      transform: translateY(-50%);
    `;
  }}
  background: #ffffff;
  border: 1.5px solid ${G.tealMid};
  border-radius: 10px;
  padding: 10px 12px;
  min-width: 200px;
  max-width: 260px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  z-index: 999;
  animation: ${fadeUp} 0.15s ease;
`;

const HyperlinkPinTitle = styled.div`
  font-size: 12px;
  color: #1e293b;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
`;

const HyperlinkPinMeta = styled.div`
  font-size: 10.5px;
  color: #64748b;
  margin-bottom: 8px;
`;

const HyperlinkPinActions = styled.div`
  display: flex;
  gap: 6px;
`;

const HyperlinkPinOpenBtn = styled.button`
  flex: 1;
  background: linear-gradient(135deg, ${G.teal}, ${G.tealDark});
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: linear-gradient(135deg, ${G.tealDark}, #004d53);
  }
`;

const HyperlinkPinDeleteBtn = styled.button`
  background: transparent;
  color: #ef4444;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 5px 9px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #fee2e2;
    border-color: #ef4444;
  }
`;

const DocHyperlinkCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 10px 12px;
  animation: ${fadeUp} 0.2s ease;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  &:hover {
    box-shadow: 0 3px 12px rgba(0, 131, 140, 0.15);
    border-color: ${G.tealMid};
    transform: translateY(-1px);
  }
`;

const DocHyperlinkTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DocHyperlinkIconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${G.tealLight};
  color: ${G.teal};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DocHyperlinkInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const DocHyperlinkTarget = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${G.teal};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover {
    color: ${G.tealDark};
  }
`;

const DocHyperlinkNoteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 6px;
  border-top: 1px dashed #e2e8f0;
`;

const DocHyperlinkAddNoteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  color: ${G.slate500 || "#64748b"};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 4px;
  align-self: flex-start;
  transition: all 0.15s ease;
  &:hover {
    color: ${G.teal};
    background: ${G.tealLight || "#f0fdfa"};
  }
`;

const DocHyperlinkNoteDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 5px 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

const DocHyperlinkNoteTextWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
`;

const DocHyperlinkNoteText = styled.span`
  font-size: 11px;
  color: ${G.slate700 || "#334155"};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocHyperlinkNoteEditBtn = styled.button`
  background: transparent;
  border: none;
  color: ${G.slate400 || "#94a3b8"};
  padding: 2px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover {
    color: ${G.teal};
  }
`;

const DocHyperlinkNoteInputWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DocHyperlinkNoteInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
`;

const DocHyperlinkNoteInput = styled.input`
  flex: 1;
  min-width: 0;
  font-size: 11px;
  padding: 5px 8px;
  border: 1px solid ${G.slate300 || "#cbd5e1"};
  border-radius: 6px;
  outline: none;
  background: #ffffff;
  color: ${G.slate800 || "#1e293b"};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  &:focus {
    border-color: ${G.teal};
    box-shadow: 0 0 0 2px rgba(0, 131, 140, 0.12);
  }
  &::placeholder {
    color: ${G.slate400 || "#94a3b8"};
    font-size: 11px;
  }
`;

const DocHyperlinkNoteMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2px;
`;

const DocHyperlinkCharCount = styled.span`
  font-size: 10px;
  color: ${(props) => (props.$nearLimit ? "#f59e0b" : G.slate400 || "#94a3b8")};
  font-weight: 500;
`;

const DocHyperlinkNoteActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DocHyperlinkNoteSaveBtn = styled.button`
  background: ${G.teal};
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${G.tealDark};
  }
`;

const DocHyperlinkNoteCancelBtn = styled.button`
  background: transparent;
  color: ${G.slate500 || "#64748b"};
  border: 1px solid ${G.slate200 || "#e2e8f0"};
  border-radius: 4px;
  font-size: 10px;
  padding: 3px 6px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #f1f5f9;
    color: ${G.slate700 || "#334155"};
  }
`;

// ─── Interactive viewing chrome ──────────────────────────────────────────────

const ShortcutsBtn = styled.button`
  width: 32px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${({ $active }) => ($active ? "#00818a" : "#e8edf3")};
  border-radius: 7px;
  background: ${({ $active }) => ($active ? "#f0fafa" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    background: #f0fafa;
    border-color: #00818a55;
    color: #00818a;
  }
  /* Keyboard help is irrelevant on touch-only devices. */
  @media (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const ReadProgress = styled.div`
  height: 3px;
  background: rgba(0, 129, 138, 0.1);
  flex-shrink: 0;
  position: relative;
  z-index: 19;
`;

const ReadProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #00a3ad, #00818a);
  border-radius: 0 3px 3px 0;
  transition: width 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ZoomToast = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 40;
  pointer-events: none;
  padding: 10px 18px;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.78);
  color: #ffffff;
  font-size: 18px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: 0.02em;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.3);
  backdrop-filter: blur(6px);
  animation: ${toastPop} 0.15s ease;
`;

const ShortcutsSheet = styled.div`
  position: absolute;
  top: 64px;
  right: 16px;
  z-index: 45;
  width: min(340px, calc(100% - 24px));
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
  overflow: hidden;
  animation: ${fadeUp} 0.18s ease;

  @media (max-width: 900px) {
    top: auto;
    bottom: 16px;
    left: 12px;
    right: 12px;
    width: auto;
    max-width: 420px;
    margin: 0 auto;
  }
`;

const ShortcutsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
  font-size: 12.5px;
  color: #1e293b;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ShortcutsClose = styled.button`
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border: none;
  background: none;
  border-radius: 6px;
  color: #94a3b8;
  cursor: pointer;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

const ShortcutList = styled.div`
  padding: 6px 14px;
  max-height: 50vh;
  overflow-y: auto;
`;

const ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  font-size: 12px;
  color: #475569;
  & + & {
    border-top: 1px dashed #eef2f6;
  }
  & > span:last-child {
    display: inline-flex;
    gap: 4px;
    flex-shrink: 0;
  }
`;

const Kbd = styled.kbd`
  min-width: 22px;
  padding: 2px 6px;
  border-radius: 5px;
  border: 1px solid #dbe3ec;
  border-bottom-width: 2px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-align: center;
`;

const ShortcutFoot = styled.div`
  padding: 10px 14px 12px;
  font-size: 11px;
  line-height: 1.5;
  color: #94a3b8;
  background: #fafbfc;
  border-top: 1px solid #f1f5f9;
`;
